import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  CalendarDaysIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserGroupIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CogIcon,
  ChartBarIcon,
  CalendarIcon,
  UserIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
  XMarkIcon,
  DocumentTextIcon,
  UsersIcon,
  ScaleIcon,
  ClipboardDocumentListIcon,
  UserPlusIcon,
  AdjustmentsHorizontalIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleSolidIcon,
  XCircleIcon as XCircleSolidIcon,
  ClockIcon as ClockSolidIcon
} from '@heroicons/react/24/solid';
import { Helmet } from 'react-helmet-async';
import {
  useGetLeaveRequestsQuery,
  useGetLeaveTypesQuery,
  useGetLeaveBalancesQuery,
  useCreateLeaveRequestMutation,
  useApproveLeaveRequestMutation,
  useRejectLeaveRequestMutation,
  useCreateLeaveTypeMutation,
  useUpdateLeaveTypeMutation,
  useDeleteLeaveTypeMutation,
  useAccrueLeavesMutation,
  useCarryForwardLeaveBalancesMutation,
  useValidateCarryForwardMutation,
  useGetCarryForwardHistoryQuery,
  useUpdateLeaveBalanceMutation,
  useUpdateLeaveRequestMutation,
  useGetLeaveCalendarQuery
} from '../../services/leaveApi';
import {
  useGetEmployeesQuery
} from '../../services/employeeApi';
import {
  useGetHolidaysForCalendarQuery
} from '../../services/holidayApi';
import { io } from 'socket.io-client';

// Leave Calendar Component
const LeaveCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'day'
  const [filters, setFilters] = useState({
    employeeId: '',
    departmentId: '',
    leaveType: '',
    employeeSearch: ''
  });
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [showLegend, setShowLegend] = useState(false);

  const { data: calendarData, isLoading, refetch } = useGetLeaveCalendarQuery({
    month: currentDate.getMonth(),
    year: currentDate.getFullYear(),
    ...filters
  });

  const { data: employeesData, isLoading: employeesLoading } = useGetEmployeesQuery();
  const { data: leaveTypesData } = useGetLeaveTypesQuery();
  const { data: holidaysData } = useGetHolidaysForCalendarQuery({
    month: currentDate.getMonth() + 1, // API expects 1-based month
    year: currentDate.getFullYear()
  });

  const employees = employeesData?.data?.employees || [];
  const leaveTypes = leaveTypesData?.data?.leaveTypes || [];
  const calendarEvents = calendarData?.data?.events || [];
  const holidays = holidaysData?.data || [];

  // Filter employees based on search
  const filteredEmployees = employees.filter(employee => {
    if (!filters.employeeSearch) return true;
    const searchTerm = filters.employeeSearch.toLowerCase();
    return (
      employee.firstName.toLowerCase().includes(searchTerm) ||
      employee.lastName.toLowerCase().includes(searchTerm) ||
      employee.employeeId.toLowerCase().includes(searchTerm) ||
      `${employee.firstName} ${employee.lastName}`.toLowerCase().includes(searchTerm)
    );
  });

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.employee-dropdown-container')) {
        setShowEmployeeDropdown(false);
      }
      if (!event.target.closest('.legend-container')) {
        setShowLegend(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setShowEmployeeDropdown(false);
        setShowLegend(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  // Calendar navigation functions
  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const current = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  // Get events for a specific day
  const getEventsForDay = (day) => {
    // Get leave events
    const leaveEvents = calendarEvents.filter(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      return day >= eventStart && day <= eventEnd;
    });

    // Get holidays for this day
    const dayHolidays = holidays.filter(holiday => {
      const holidayDate = new Date(holiday.date);
      return holidayDate.toDateString() === day.toDateString();
    });

    // Combine and return both types of events
    return [...leaveEvents, ...dayHolidays];
  };

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-8">
      {/* Beautiful Header with Gradient */}
      <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-3xl shadow-2xl">
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="relative px-8 py-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <div className="text-white">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center">
                  <CalendarIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold">Leave Calendar</h1>
                  <p className="text-blue-100 text-lg">Beautiful overview of team leave schedules</p>
                </div>
              </div>
              <div className="flex items-center space-x-4 mt-4">
                <div className="bg-white bg-opacity-20 rounded-xl px-4 py-2">
                  <span className="text-white font-semibold">{calendarEvents.length} Leave Events</span>
                </div>
                <div className="bg-white bg-opacity-20 rounded-xl px-4 py-2">
                  <span className="text-white font-semibold">{leaveTypes.length} Leave Types</span>
                </div>
              </div>
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center justify-between bg-white bg-opacity-10 rounded-2xl p-4 backdrop-blur-sm">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigateMonth(-1)}
                  className="w-12 h-12 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
                >
                  <ChevronLeftIcon className="h-6 w-6 text-white" />
                </button>

                <div className="text-center px-6">
                  <div className="text-2xl font-bold text-white">
                    {formatMonthYear(currentDate)}
                  </div>
                  <button
                    onClick={goToToday}
                    className="mt-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg font-medium transition-all duration-200 hover:scale-105"
                  >
                    Today
                  </button>
                </div>

                <button
                  onClick={() => navigateMonth(1)}
                  className="w-12 h-12 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
                >
                  <ChevronLeftIcon className="h-6 w-6 text-white rotate-180" />
                </button>
              </div>
            </div>

            {/* Legend Button - Separate from Navigation */}
            <div className="relative legend-container ml-4">
              <button
                onClick={() => setShowLegend(!showLegend)}
                className="flex items-center space-x-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-xl transition-all duration-200 hover:scale-105 backdrop-blur-sm border border-white border-opacity-20"
              >
                <div className="flex items-center space-x-1">
                  {leaveTypes.slice(0, 3).map(type => (
                    <div
                      key={type._id}
                      className="w-2 h-2 rounded-full shadow-sm"
                      style={{ backgroundColor: type.color || '#3B82F6' }}
                    ></div>
                  ))}
                  {leaveTypes.length > 3 && (
                    <span className="text-xs text-white ml-1 font-medium">+{leaveTypes.length - 3}</span>
                  )}
                </div>
                <span className="text-sm font-semibold text-white">Legend</span>
                <ChevronDownIcon className={`h-4 w-4 text-white transition-transform ${showLegend ? 'rotate-180' : ''}`} />
              </button>

              {/* Enhanced Legend Dropdown - Better Positioning */}
              {showLegend && (
                <div className="absolute top-full right-0 mt-4 w-96 bg-white rounded-xl shadow-2xl border-2 border-blue-200 p-5 z-[200] animate-in slide-in-from-top-2 duration-200">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <span>Leave Types Legend</span>
                    </h4>
                    <button
                      onClick={() => setShowLegend(false)}
                      className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1 transition-colors"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {leaveTypes.map(type => (
                      <div
                        key={type._id}
                        className="flex items-center space-x-2 p-3 bg-gradient-to-r from-gray-50 to-white rounded-lg hover:from-blue-50 hover:to-indigo-50 border border-gray-100 hover:border-blue-200 transition-all duration-200 cursor-pointer group"
                      >
                        <div
                          className="w-4 h-4 rounded-full shadow-md ring-2 ring-white group-hover:ring-blue-200 transition-all duration-200"
                          style={{ backgroundColor: type.color || '#3B82F6' }}
                        ></div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-gray-900 truncate group-hover:text-blue-900">{type.name}</div>
                          <div className="text-xs text-gray-500 font-medium">{type.code}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Holiday Types Section */}
                  <div className="mb-4">
                    <h5 className="text-xs font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>Holiday Types</span>
                    </h5>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { type: 'national', label: 'National', color: '#DC2626', icon: '🇮🇳' },
                        { type: 'regional', label: 'Regional', color: '#EA580C', icon: '🏛️' },
                        { type: 'company', label: 'Company', color: '#2563EB', icon: '🏢' },
                        { type: 'religious', label: 'Religious', color: '#7C3AED', icon: '🙏' },
                        { type: 'other', label: 'Other', color: '#059669', icon: '📅' }
                      ].map(holidayType => (
                        <div
                          key={holidayType.type}
                          className="flex items-center space-x-2 p-2 bg-gradient-to-r from-gray-50 to-white rounded-lg hover:from-green-50 hover:to-emerald-50 border border-gray-100 hover:border-green-200 transition-all duration-200 cursor-pointer group"
                        >
                          <div className="text-sm">{holidayType.icon}</div>
                          <div
                            className="w-3 h-3 rounded-full shadow-md ring-1 ring-white group-hover:ring-green-200 transition-all duration-200"
                            style={{ backgroundColor: holidayType.color }}
                          ></div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-gray-900 truncate group-hover:text-green-900">{holidayType.label}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-3">
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div className="text-xs">
                        <div className="font-bold text-gray-900 text-lg">{calendarEvents.length}</div>
                        <div className="text-gray-600 font-medium">Events</div>
                      </div>
                      <div className="text-xs">
                        <div className="font-bold text-gray-900 text-lg">{leaveTypes.length}</div>
                        <div className="text-gray-600 font-medium">Types</div>
                      </div>
                      <div className="text-xs">
                        <div className="font-bold text-gray-900 text-lg">{employees.length}</div>
                        <div className="text-gray-600 font-medium">Staff</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modern Filters */}
      <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
            <FunnelIcon className="h-5 w-5 text-white" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">Filters & Views</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">Employee</label>
            <div className="relative employee-dropdown-container">
              <input
                type="text"
                placeholder="Search employees..."
                value={
                  filters.employeeId && !filters.employeeSearch
                    ? `${employees.find(emp => emp._id === filters.employeeId)?.firstName || ''} ${employees.find(emp => emp._id === filters.employeeId)?.lastName || ''}`.trim()
                    : filters.employeeSearch || ''
                }
                onChange={(e) => {
                  const searchValue = e.target.value;
                  setFilters(prev => ({
                    ...prev,
                    employeeSearch: searchValue,
                    employeeId: searchValue ? '' : prev.employeeId // Clear selection if user starts typing
                  }));
                  if (searchValue) {
                    setShowEmployeeDropdown(true);
                  }
                }}
                onFocus={() => setShowEmployeeDropdown(true)}
                className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 pr-10 ${
                  filters.employeeId && !filters.employeeSearch
                    ? 'bg-blue-50 border-blue-300 text-blue-900 font-medium'
                    : 'bg-gray-50 focus:bg-white'
                }`}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                {filters.employeeId && !filters.employeeSearch ? (
                  <CheckCircleIcon className="h-5 w-5 text-blue-500" />
                ) : (
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                )}
              </div>

              {/* Clear Button */}
              {(filters.employeeId || filters.employeeSearch) && (
                <button
                  onClick={() => setFilters(prev => ({
                    ...prev,
                    employeeId: '',
                    employeeSearch: ''
                  }))}
                  className="absolute inset-y-0 right-0 flex items-center pr-12 text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}

              {/* Enhanced Dropdown with Better Positioning */}
              {showEmployeeDropdown && (
                <div className="absolute z-50 w-full mt-2 bg-white border-2 border-blue-200 rounded-xl shadow-2xl max-h-64 overflow-y-auto animate-in slide-in-from-top-2 duration-200">
                  {/* All Employees Option */}
                  <div
                    onClick={() => {
                      setFilters(prev => ({
                        ...prev,
                        employeeId: '',
                        employeeSearch: ''
                      }));
                      setShowEmployeeDropdown(false);
                    }}
                    className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white"
                  >
                    <div className="flex items-center space-x-3">
                      <UsersIcon className="h-5 w-5 text-blue-500" />
                      <span className="text-gray-900 font-medium">All Employees</span>
                      <span className="text-gray-500 text-sm bg-gray-100 px-2 py-1 rounded-full">({employees.length})</span>
                    </div>
                  </div>

                  {/* Filtered Employees */}
                  {filteredEmployees.slice(0, 8).map(employee => (
                    <div
                      key={employee._id}
                      onClick={() => {
                        setFilters(prev => ({
                          ...prev,
                          employeeId: employee._id,
                          employeeSearch: ''
                        }));
                        setShowEmployeeDropdown(false);
                      }}
                      className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors duration-150"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center shadow-sm">
                          <span className="text-white text-sm font-semibold">
                            {employee.firstName.charAt(0)}{employee.lastName.charAt(0)}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="text-gray-900 font-medium">
                            {employee.firstName} {employee.lastName}
                          </div>
                          <div className="text-gray-500 text-sm">
                            ID: {employee.employeeId}
                          </div>
                        </div>
                        {filters.employeeId === employee._id && (
                          <CheckCircleIcon className="h-5 w-5 text-blue-500 animate-in zoom-in-50 duration-200" />
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Show more indicator */}
                  {filteredEmployees.length > 8 && (
                    <div className="px-4 py-2 text-center text-gray-500 text-sm border-t border-gray-100 bg-gray-50">
                      <span className="font-medium">+{filteredEmployees.length - 8} more employees...</span>
                    </div>
                  )}

                  {/* No Results */}
                  {filteredEmployees.length === 0 && filters.employeeSearch && (
                    <div className="px-4 py-8 text-center text-gray-500">
                      <UserIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                      <p className="font-medium">No employees found</p>
                      <p className="text-sm">Try a different search term</p>
                    </div>
                  )}

                  {/* Loading State */}
                  {employeesLoading && (
                    <div className="px-4 py-8 text-center text-gray-500">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
                      <p className="font-medium">Loading employees...</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">Leave Type</label>
            <select
              value={filters.leaveType}
              onChange={(e) => setFilters(prev => ({ ...prev, leaveType: e.target.value }))}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
            >
              <option value="">All Types</option>
              {leaveTypes.map(type => (
                <option key={type._id} value={type._id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">View Mode</label>
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
            >
              <option value="month">Month View</option>
              <option value="week">Week View</option>
              <option value="day">Day View</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ employeeId: '', departmentId: '', leaveType: '' })}
              className="w-full px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white font-semibold rounded-xl transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Beautiful Calendar Grid */}
      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
        {isLoading ? (
          <div className="p-16 text-center">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-6"></div>
              <div className="absolute inset-0 rounded-full border-4 border-purple-200 border-t-purple-600 animate-spin mx-auto" style={{animationDirection: 'reverse', animationDuration: '1.5s'}}></div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Loading Calendar</h3>
            <p className="text-gray-600">Fetching leave schedules...</p>
          </div>
        ) : (
          <>
            {/* Calendar Header */}
            <div className="grid grid-cols-7 bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
              {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
                <div key={day} className="p-6 text-center">
                  <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">{day.slice(0, 3)}</span>
                </div>
              ))}
            </div>

            {/* Calendar Body */}
            <div className="grid grid-cols-7">
              {calendarDays.map((day, index) => {
                const dayEvents = getEventsForDay(day);
                const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                const isToday = day.toDateString() === new Date().toDateString();
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;

                return (
                  <div
                    key={index}
                    className={`min-h-[140px] p-3 border-r border-b border-gray-100 relative group transition-all duration-300 ${
                      !isCurrentMonth
                        ? 'bg-gray-50 opacity-60'
                        : isToday
                          ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'
                          : isWeekend
                            ? 'bg-gradient-to-br from-gray-50 to-slate-50'
                            : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    {/* Day Number */}
                    <div className={`text-lg font-bold mb-3 transition-all duration-200 ${
                      isCurrentMonth
                        ? isToday
                          ? 'text-blue-600 bg-blue-100 rounded-lg px-2 py-1 inline-block'
                          : 'text-gray-900'
                        : 'text-gray-400'
                    }`}>
                      {day.getDate()}
                    </div>

                    {/* Events */}
                    <div className="space-y-2">
                      {dayEvents.slice(0, 3).map((event, eventIndex) => (
                        <div
                          key={event.id || event._id}
                          className="relative group/event cursor-pointer transform hover:scale-105 transition-all duration-200"
                          onClick={() => setSelectedEvent(event)}
                        >
                          <div
                            className="text-xs p-2 rounded-lg shadow-sm border-l-4 hover:shadow-md transition-all duration-200"
                            style={{
                              backgroundColor: event.color + '15',
                              borderLeftColor: event.color,
                              color: event.color
                            }}
                          >
                            {event.holidayType ? (
                              // Holiday event
                              <div>
                                <div className="font-semibold truncate">
                                  🎉 {event.title}
                                </div>
                                <div className="text-gray-600 truncate text-xs">
                                  {event.type}
                                </div>
                              </div>
                            ) : (
                              // Leave event
                              <div>
                                <div className="font-semibold truncate">
                                  {event.employeeName.split(' ')[0]}
                                </div>
                                <div className="text-gray-600 truncate text-xs">
                                  {event.leaveTypeCode}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Tooltip */}
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover/event:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none">
                            {event.holidayType ? (
                              // Holiday tooltip
                              <div>
                                <div className="font-semibold">{event.title}</div>
                                <div className="text-gray-300">{event.type} Holiday</div>
                                {event.description && (
                                  <div className="text-gray-400 text-xs mt-1">{event.description}</div>
                                )}
                              </div>
                            ) : (
                              // Leave tooltip
                              <div>
                                {event.employeeName} - {event.leaveType}
                              </div>
                            )}
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      ))}

                      {dayEvents.length > 3 && (
                        <div className="text-xs text-gray-500 font-medium bg-gray-100 rounded-lg px-2 py-1 text-center hover:bg-gray-200 transition-colors cursor-pointer">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>

                    {/* Today Indicator */}
                    {isToday && (
                      <div className="absolute top-2 right-2 w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Enhanced Event Details Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm" onClick={() => setSelectedEvent(null)}></div>

            <div className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full mx-4 transform transition-all duration-300 scale-100">
              {/* Modal Header with Gradient */}
              <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-3xl px-8 py-6">
                <div className="absolute inset-0 bg-black bg-opacity-10"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
                      style={{ backgroundColor: selectedEvent.color + '20' }}
                    >
                      {selectedEvent.holidayType ? (
                        <CalendarDaysIcon className="h-6 w-6" style={{ color: selectedEvent.color }} />
                      ) : (
                        <UserIcon className="h-6 w-6" style={{ color: selectedEvent.color }} />
                      )}
                    </div>
                    <div>
                      {selectedEvent.holidayType ? (
                        // Holiday header
                        <>
                          <h3 className="text-xl font-bold text-white">{selectedEvent.title}</h3>
                          <p className="text-blue-100">{selectedEvent.type} Holiday</p>
                        </>
                      ) : (
                        // Leave header
                        <>
                          <h3 className="text-xl font-bold text-white">{selectedEvent.employeeName}</h3>
                          <p className="text-blue-100">{selectedEvent.leaveType}</p>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedEvent(null)}
                    className="w-10 h-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-110"
                  >
                    <XMarkIcon className="h-5 w-5 text-white" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="px-8 py-6">
                {selectedEvent.holidayType ? (
                  // Holiday Modal Content
                  <div className="space-y-6">
                    {/* Holiday Date */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-100 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <CalendarIcon className="h-4 w-4 text-green-600" />
                        <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">Holiday Date</span>
                      </div>
                      <p className="text-sm font-bold text-green-900">
                        {new Date(selectedEvent.date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    </div>

                    {/* Holiday Type */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-100 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <CalendarDaysIcon className="h-4 w-4 text-blue-600" />
                        <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Holiday Type</span>
                      </div>
                      <p className="text-sm font-bold text-blue-900 capitalize">{selectedEvent.type}</p>
                    </div>

                    {/* Holiday Description */}
                    {selectedEvent.description && (
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <DocumentTextIcon className="h-4 w-4 text-purple-600" />
                          <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Description</span>
                        </div>
                        <p className="text-sm text-purple-900 leading-relaxed">{selectedEvent.description}</p>
                      </div>
                    )}

                    {/* Holiday Status */}
                    <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <CheckCircleIcon className="h-4 w-4 text-gray-600" />
                        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Status</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${selectedEvent.isActive ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedEvent.isActive ? 'Active' : 'Inactive'}
                        </p>
                      </div>
                    </div>

                    {/* Created Information */}
                    {selectedEvent.createdBy && (
                      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <UserIcon className="h-4 w-4 text-indigo-600" />
                          <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Created By</span>
                        </div>
                        <p className="text-sm text-indigo-900">
                          Admin User
                        </p>
                        <p className="text-xs text-indigo-600 mt-1">
                          {new Date(selectedEvent.createdAt || new Date()).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  // Leave Modal Content
                  <div className="space-y-6">
                    {/* Leave Details */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <CalendarIcon className="h-4 w-4 text-blue-600" />
                          <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">From</span>
                        </div>
                        <p className="text-sm font-bold text-blue-900">
                          {new Date(selectedEvent.start).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      </div>

                      <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <CalendarIcon className="h-4 w-4 text-green-600" />
                          <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">To</span>
                        </div>
                        <p className="text-sm font-bold text-green-900">
                          {new Date(selectedEvent.end).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>

                    {/* Duration */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <ClockIcon className="h-4 w-4 text-purple-600" />
                        <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Duration</span>
                      </div>
                      <p className="text-lg font-bold text-purple-900">{selectedEvent.workingDays} Working Days</p>
                    </div>

                    {/* Employee Info */}
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <UserIcon className="h-4 w-4 text-indigo-600" />
                        <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Employee Details</span>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-indigo-900"><span className="font-medium">Name:</span> {selectedEvent.employeeName}</p>
                        <p className="text-sm text-indigo-900"><span className="font-medium">ID:</span> {selectedEvent.employeeId}</p>
                      </div>
                    </div>

                    {/* Reason */}
                    {selectedEvent.reason && (
                      <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <DocumentTextIcon className="h-4 w-4 text-gray-600" />
                          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Reason</span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{selectedEvent.reason}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

const AdminLeaveManagement = () => {
  const { t } = useTranslation(['common', 'leave']);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  // State management
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showEmployeeRequestModal, setShowEmployeeRequestModal] = useState(false);
  const [showLeaveBalanceModal, setShowLeaveBalanceModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState(''); // 'approve' or 'reject'
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [selectedType, setSelectedType] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showCarryForwardModal, setShowCarryForwardModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    leaveType: '',
    startDate: '',
    endDate: '',
    employee: '',
    search: '',
    department: ''
  });
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0
  });
  const [showFilters, setShowFilters] = useState(false);

  // Form states
  const [leaveForm, setLeaveForm] = useState({
    employeeId: '',
    leaveTypeId: '',
    fromDate: '',
    toDate: '',
    reason: '',
    deductBalanceImmediately: false, // New option for immediate balance deduction
    contactDuringLeave: {
      phone: '',
      email: '',
      address: ''
    }
  });

  const [typeForm, setTypeForm] = useState({
    name: '',
    code: '',
    countryCode: 'IN', // Default to India
    description: '',
    isPaid: true,
    accrualType: 'yearly',
    accrualAmount: 0,
    maxCarryForward: 0,
    maxEncashment: 0,
    applicationRules: {
      minDays: 1,
      maxDays: 0,
      includeWeekendsInCalculation: false
    }
  });

  const [bulkForm, setBulkForm] = useState({
    leaveTypeId: '',
    allocationType: 'fixed', // fixed, percentage, service_based
    allocationValue: 0,
    employeeIds: [],
    year: new Date().getFullYear()
  });

  const [carryForwardForm, setCarryForwardForm] = useState({
    leaveTypeId: '',
    fromYear: new Date().getFullYear() - 1,
    toYear: new Date().getFullYear(),
    employeeIds: [],
    maxCarryForwardDays: 0,
    applyToAllEligible: true
  });

  const { data: carryForwardHistoryData, isLoading: carryForwardHistoryLoading } = useGetCarryForwardHistoryQuery({
    page: 1,
    limit: 10
  });

  const [editForm, setEditForm] = useState({
    leaveTypeId: '',
    fromDate: '',
    toDate: '',
    reason: '',
    contactDuringLeave: {
      phone: '',
      email: '',
      address: ''
    }
  });

  const [approvalForm, setApprovalForm] = useState({
    remarks: '',
    action: '' // 'approve' or 'reject'
  });

  const [notification, setNotification] = useState({
    show: false,
    type: '', // 'success', 'error'
    message: ''
  });

  const [currentLeaveBalance, setCurrentLeaveBalance] = useState(null);

  // API hooks
  const { data: leaveRequestsData, isLoading: requestsLoading, refetch } = useGetLeaveRequestsQuery({
    search: filters.search,
    ...filters,
    page: pagination.page,
    limit: pagination.limit
  });

  const { data: leaveTypesData, isLoading: typesLoading } = useGetLeaveTypesQuery();

  const { data: employeesData, isLoading: employeesLoading } = useGetEmployeesQuery();

  const employeeId = selectedEmployee?._id || selectedEmployee?.id || '';
  const { data: leaveBalancesData, isLoading: balancesLoading, error: balancesError } = useGetLeaveBalancesQuery(
    { employeeId, year: new Date().getFullYear() },
    { 
      skip: !employeeId,
      refetchOnMountOrArgChange: true
    }
  );

  // Query for form employee balance
  const { data: formEmployeeBalancesData, isLoading: formBalancesLoading } = useGetLeaveBalancesQuery(
    { employeeId: leaveForm.employeeId, year: new Date().getFullYear() },
    { 
      skip: !leaveForm.employeeId,
      refetchOnMountOrArgChange: true
    }
  );

  const [createLeaveRequest, { isLoading: creatingRequest }] = useCreateLeaveRequestMutation();
  const [approveRequest, { isLoading: approving }] = useApproveLeaveRequestMutation();
  const [rejectRequest, { isLoading: rejecting }] = useRejectLeaveRequestMutation();
  const [createLeaveType, { isLoading: creatingType }] = useCreateLeaveTypeMutation();
  const [updateLeaveType, { isLoading: updatingType }] = useUpdateLeaveTypeMutation();
  const [deleteLeaveType, { isLoading: deletingType }] = useDeleteLeaveTypeMutation();
  const [accrueLeaves, { isLoading: accruing }] = useAccrueLeavesMutation();
  const [updateLeaveBalance, { isLoading: updatingBalance }] = useUpdateLeaveBalanceMutation();
  const [updateLeaveRequest, { isLoading: updatingRequest }] = useUpdateLeaveRequestMutation();
  const [carryForwardLeaveBalances, { isLoading: carryingForward }] = useCarryForwardLeaveBalancesMutation();
  const [validateCarryForward, { isLoading: validating }] = useValidateCarryForwardMutation();

  const leaveRequests = leaveRequestsData?.data?.leaveRequests || [];
  const leaveTypes = leaveTypesData?.data?.leaveTypes || [];
  const employees = employeesData?.data?.employees || [];
  const leaveBalances = leaveBalancesData?.data?.balances || [];

  // Update pagination state when data changes
  useEffect(() => {
    if (leaveRequestsData?.data?.pagination) {
      const apiPagination = leaveRequestsData.data.pagination;
      setPagination(prev => ({
        ...prev,
        total: apiPagination.totalRecords || 0,
        totalPages: apiPagination.totalPages || 1
      }));
    }
  }, [leaveRequestsData]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Refetch when filters or pagination change
  useEffect(() => {
    refetch();
  }, [filters, pagination.page, pagination.limit, refetch]);

  // Socket connection for real-time updates
  useEffect(() => {
    const socketUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5001';
    const socket = io(socketUrl, {
      query: {
        token: user?.token
      }
    });

    // Listen for leave request status changes (admin should refetch leave list)
    socket.on('leave:request-status-changed', (data) => {
      console.log('🔄 Leave request status changed:', data);
      refetch(); // Refetch the leave requests list immediately

      // Show a brief notification
      setNotification({
        show: true,
        type: 'info',
        message: `Leave request for ${data.employeeName} has been ${data.changeType}d`
      });

      setTimeout(() => {
        setNotification({ show: false, type: '', message: '' });
      }, 3000);
    });

    // Listen for new leave requests (admin should refetch leave list)
    socket.on('leave:new-request', (data) => {
      console.log('📧 New leave request created:', data);
      refetch(); // Refetch the leave requests list immediately
    });

    // Listen for leave request cancellations (admin should refetch leave list)
    socket.on('leave:request-cancelled', (data) => {
      console.log('❌ Leave request cancelled:', data);
      refetch(); // Refetch the leave requests list immediately
    });

    return () => {
      socket.disconnect();
    };
  }, [user?.token, refetch]);

  // Effect to update current leave balance when employee or leave type changes
  useEffect(() => {
    const formEmployeeBalances = formEmployeeBalancesData?.data?.balances || [];
    if (leaveForm.employeeId && leaveForm.leaveTypeId && formEmployeeBalances.length > 0) {
      const employeeBalance = formEmployeeBalances.find(
        balance => balance.leaveTypeId._id === leaveForm.leaveTypeId
      );
      setCurrentLeaveBalance(employeeBalance || null);
    } else {
      setCurrentLeaveBalance(null);
    }
  }, [leaveForm.employeeId, leaveForm.leaveTypeId, formEmployeeBalancesData]);

  // Handlers
  const handleCreateLeaveRequest = async (e) => {
    e.preventDefault();
    try {
      await createLeaveRequest(leaveForm).unwrap();
      setShowAddModal(false);
      resetLeaveForm();
    } catch (error) {
      console.error('Error creating leave request:', error);
    }
  };

  const handleCreateEmployeeRequest = async (e) => {
    e.preventDefault();
    
    // Check if leave balance is zero
    if (currentLeaveBalance && currentLeaveBalance.available <= 0) {
      setNotification({
        show: true,
        type: 'error',
        message: 'Cannot create leave request. Employee has no available leave balance for this leave type.'
      });
      return;
    }
    
    try {
      await createLeaveRequest(leaveForm).unwrap();
      setShowEmployeeRequestModal(false);
      resetLeaveForm();
      setCurrentLeaveBalance(null);
      setNotification({
        show: true,
        type: 'success',
        message: 'Leave request created successfully!'
      });
    } catch (error) {
      console.error('Error creating leave request for employee:', error);
      setNotification({
        show: true,
        type: 'error',
        message: error.data?.message || 'Failed to create leave request'
      });
    }
  };

  const handleBulkAllocation = async (e) => {
    e.preventDefault();
    try {
      // Process bulk allocation for selected employees
      for (const employeeId of bulkForm.employeeIds) {
        await updateLeaveBalance({
          employeeId,
          leaveTypeId: bulkForm.leaveTypeId,
          year: bulkForm.year,
          allocated: bulkForm.allocationValue,
          used: 0,
          available: bulkForm.allocationValue,
          carriedForward: 0,
          encashed: 0
        }).unwrap();
      }
      setShowBulkModal(false);
      resetBulkForm();
    } catch (error) {
      console.error('Error in bulk allocation:', error);
    }
  };

  const handleApproveRequest = (request) => {
    setSelectedRequest(request);
    setApprovalAction('approve');
    setApprovalForm({ remarks: '', action: 'approve' });
    setShowApprovalModal(true);
  };

  const handleRejectRequest = (request) => {
    setSelectedRequest(request);
    setApprovalAction('reject');
    setApprovalForm({ remarks: '', action: 'reject' });
    setShowApprovalModal(true);
  };

  const handleApprovalSubmit = async (e) => {
    e.preventDefault();
    try {
      if (approvalAction === 'approve') {
        await approveRequest({
          id: selectedRequest._id,
          remarks: approvalForm.remarks || 'Approved by admin'
        }).unwrap();
        setNotification({
          show: true,
          type: 'success',
          message: `Leave request for ${selectedRequest.employeeId.firstName} ${selectedRequest.employeeId.lastName} has been approved successfully!`
        });
      } else {
        await rejectRequest({
          id: selectedRequest._id,
          remarks: approvalForm.remarks || 'Rejected by admin'
        }).unwrap();
        setNotification({
          show: true,
          type: 'success',
          message: `Leave request for ${selectedRequest.employeeId.firstName} ${selectedRequest.employeeId.lastName} has been rejected.`
        });
      }
      setShowApprovalModal(false);
      setSelectedRequest(null);
      setApprovalForm({ remarks: '', action: '' });
      
      // Hide notification after 5 seconds
      setTimeout(() => {
        setNotification({ show: false, type: '', message: '' });
      }, 5000);
    } catch (error) {
      console.error('Error processing leave request:', error);
      setNotification({
        show: true,
        type: 'error',
        message: `Failed to ${approvalAction} the leave request. Please try again.`
      });
      setTimeout(() => {
        setNotification({ show: false, type: '', message: '' });
      }, 5000);
    }
  };

  const handleCarryForward = async (e) => {
    e.preventDefault();
    try {
      const carryForwardData = {
        sourceYear: carryForwardForm.fromYear,
        targetYear: carryForwardForm.toYear,
        employeeIds: carryForwardForm.applyToAllEligible ? [] : carryForwardForm.employeeIds,
        leaveTypeIds: carryForwardForm.leaveTypeId ? [carryForwardForm.leaveTypeId] : []
      };

      const result = await carryForwardLeaveBalances(carryForwardData).unwrap();

      setNotification({
        show: true,
        type: 'success',
        message: `Carry forward operation completed successfully! ${result.data.totalCarried || 0} days carried forward for ${result.data.results?.filter(r => r.success).length || 0} employees`
      });

      setShowCarryForwardModal(false);
      setCarryForwardForm({
        leaveTypeId: '',
        fromYear: new Date().getFullYear() - 1,
        toYear: new Date().getFullYear(),
        employeeIds: [],
        maxCarryForwardDays: 0,
        applyToAllEligible: true
      });

      // Refresh carry forward history
      refetch();

      setTimeout(() => {
        setNotification({ show: false, type: '', message: '' });
      }, 5000);
    } catch (error) {
      console.error('Error carrying forward leaves:', error);
      setNotification({
        show: true,
        type: 'error',
        message: error.data?.message || 'Failed to carry forward leave balances. Please try again.'
      });
      setTimeout(() => {
        setNotification({ show: false, type: '', message: '' });
      }, 5000);
    }
  };

  const handleEditRequest = async (e) => {
    e.preventDefault();
    try {
      await updateLeaveRequest({
        id: selectedRequest._id,
        ...editForm
      }).unwrap();
      setShowEditModal(false);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error updating leave request:', error);
    }
  };

  const handleCreateLeaveType = async (e) => {
    e.preventDefault();
    try {
      await createLeaveType(typeForm).unwrap();
      setShowTypeModal(false);
      resetTypeForm();
    } catch (error) {
      console.error('Error creating leave type:', error);
    }
  };

  const handleDeleteLeaveType = async (e) => {
    e.preventDefault();
    if (!selectedType || confirmationText !== selectedType.name) return;

    try {
      await deleteLeaveType(selectedType._id).unwrap();

      setNotification({
        show: true,
        type: 'success',
        message: 'Leave type deleted successfully. All associated data has been preserved.'
      });

      setShowDeleteModal(false);
      setSelectedType(null);
      setConfirmationText('');

      // Hide notification after 5 seconds
      setTimeout(() => {
        setNotification({ show: false, type: '', message: '' });
      }, 5000);

    } catch (error) {
      console.error('Error deleting leave type:', error);
      setNotification({
        show: true,
        type: 'error',
        message: error.data?.message || 'Failed to delete leave type. Please try again.'
      });

      setTimeout(() => {
        setNotification({ show: false, type: '', message: '' });
      }, 5000);
    }
  };

  const resetLeaveForm = () => {
    setLeaveForm({
      employeeId: '',
      leaveTypeId: '',
      fromDate: '',
      toDate: '',
      reason: '',
      deductBalanceImmediately: false,
      contactDuringLeave: {
        phone: '',
        email: '',
        address: ''
      }
    });
    setCurrentLeaveBalance(null);
  };

  const resetTypeForm = () => {
    setTypeForm({
      name: '',
      code: '',
      countryCode: 'IN', // Default to India
      description: '',
      isPaid: true,
      accrualType: 'yearly',
      accrualAmount: 0,
      maxCarryForward: 0,
      isEncashmentAllowed: false,
      maxEncashment: 0,
      applicationRules: {
        minDays: 1,
        maxDays: 0,
        includeWeekendsInCalculation: false
      }
    });
  };

  // Auto-generate code from name
  const generateCodeFromName = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 4); // Limit to 4 characters max
  };

  const handleNameChange = (value) => {
    const generatedCode = generateCodeFromName(value);
    setTypeForm(prev => ({
      ...prev,
      name: value,
      code: generatedCode
    }));
  };

  const resetBulkForm = () => {
    setBulkForm({
      leaveTypeId: '',
      allocationType: 'fixed',
      allocationValue: 0,
      employeeIds: [],
      year: new Date().getFullYear()
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon },
    { id: 'requests', label: 'Leave Requests', icon: ClipboardDocumentListIcon },
    { id: 'calendar', label: 'Leave Calendar', icon: CalendarIcon },
    { id: 'types', label: 'Leave Types', icon: CogIcon },
    { id: 'allocation', label: 'Bulk Allocation', icon: ScaleIcon },
    { id: 'carry-forward', label: 'Carry Forward', icon: ArrowTrendingUpIcon },
    { id: 'employees', label: 'Employee Leave', icon: UsersIcon }
  ];

  return (
    <>
      {/* Notification Toast */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-[60] max-w-sm w-full ${
          notification.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
        } border rounded-lg p-4 shadow-lg`}>
          <div className="flex items-center">
            <div className={`flex-shrink-0 ${
              notification.type === 'success' ? 'text-green-400' : 'text-red-400'
            }`}>
              {notification.type === 'success' ? (
                <CheckCircleIcon className="h-5 w-5" />
              ) : (
                <XCircleIcon className="h-5 w-5" />
              )}
            </div>
            <div className="ml-3">
              <p className={`text-sm font-medium ${
                notification.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {notification.message}
              </p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setNotification({ show: false, type: '', message: '' })}
                className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  notification.type === 'success' 
                    ? 'text-green-500 hover:bg-green-100 focus:ring-green-600' 
                    : 'text-red-500 hover:bg-red-100 focus:ring-red-600'
                }`}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <Helmet>
        <title>{t('leave:admin_leave_management')} - {t('common:app_name')}</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-7xl mx-auto px-2 sm:px-3 lg:px-4 py-2">
        {/* Ultra Compact Header */}
        <div className="flex items-center justify-between mb-3 py-2">
          <div className="flex items-center space-x-2">
            <div className="bg-blue-600 rounded-lg p-1.5">
              <AdjustmentsHorizontalIcon className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Leave Admin</h1>
              <p className="text-xs text-gray-600 hidden md:block">Manage leave policies, requests & balances</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {/* Activity indicator */}
            <div className="flex items-center space-x-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-md">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
              <span className="hidden lg:inline font-medium">Live</span>
            </div>
            {/* Quick stats */}
            <div className="hidden md:flex items-center space-x-4 text-xs text-gray-500">
              <span>{leaveTypes.length} Types</span>
              <span className="text-gray-300">|</span>
              <span>{leaveRequests.length} Requests</span>
              <span className="text-gray-300">|</span>
              <span>{employees.length} Employees</span>
            </div>
          </div>
        </div>

        {/* Compact Tabs */}
        <div className="mb-3">
          <div className="bg-gray-100 rounded-lg p-1 border border-gray-200">
            <div className="grid grid-cols-7 gap-1">
              {tabs.map((tab) => {
                // Custom abbreviations for tabs that would otherwise show "Leave"
                const getTabAbbrev = (tabId, label) => {
                  switch (tabId) {
                    case 'overview': return '';
                    case 'requests': return 'Req';
                    case 'calendar': return 'Cal';
                    case 'types': return 'Types';
                    case 'allocation': return 'Alloc';
                    case 'carry-forward': return 'CFwd';
                    case 'employees': return 'Emp';
                    default: return label.split(' ')[0];
                  }
                };

                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center justify-center px-2 py-2 rounded-md transition-all duration-200 ${
                      activeTab === tab.id
                        ? 'bg-white text-blue-600 shadow-sm border border-blue-200'
                        : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                    }`}
                    title={tab.label} // Show full label on hover
                  >
                    <tab.icon className={`${activeTab === tab.id ? 'h-4 w-4' : 'h-3 w-3'} ${tab.id !== 'overview' ? 'mr-1' : ''}`} />
                    <span className={`text-xs font-medium ${tab.id === 'overview' ? 'hidden' : ''}`}>
                      {getTabAbbrev(tab.id, tab.label)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-4">
              {/* Compact Overview Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Leave Types</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{leaveTypes.length}</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-2">
                      <CogIcon className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pending</p>
                      <p className="text-2xl font-bold text-yellow-600 mt-1">
                        {leaveRequests.filter(r => r.status === 'pending').length}
                      </p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-2">
                      <ClockIcon className="h-4 w-4 text-yellow-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Approved</p>
                      <p className="text-2xl font-bold text-green-600 mt-1">
                        {leaveRequests.filter(r => r.status === 'approved').length}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2">
                      <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Employees</p>
                      <p className="text-2xl font-bold text-indigo-600 mt-1">{employees.length}</p>
                    </div>
                    <div className="bg-indigo-50 rounded-lg p-2">
                      <UsersIcon className="h-4 w-4 text-indigo-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Compact Quick Actions */}
              <div className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setShowEmployeeRequestModal(true)}
                    className="flex items-center px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    <UserPlusIcon className="h-4 w-4 mr-2" />
                    Create Request
                  </button>
                  <button
                    onClick={() => setShowBulkModal(true)}
                    className="flex items-center px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    <ScaleIcon className="h-4 w-4 mr-2" />
                    Bulk Allocate
                  </button>
                  <button
                    onClick={() => setShowTypeModal(true)}
                    className="flex items-center px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Type
                  </button>
                  <button
                    onClick={() => setActiveTab('requests')}
                    className="flex items-center px-3 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
                    Manage Requests
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'calendar' && (
            <LeaveCalendar />
          )}

          {activeTab === 'requests' && (
            <div className="space-y-6">
              {/* Modern Header with Actions */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">All Leave Requests</h2>
                    <p className="text-gray-600 mt-1">Manage and track all leave applications across the organization</p>
                  </div>
                  <button
                    onClick={() => setShowEmployeeRequestModal(true)}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create Request
                  </button>
                </div>
              </div>

              {/* Filter Controls */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  {/* Leave Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Leave Type</label>
                    <select
                      value={filters.leaveType}
                      onChange={(e) => setFilters(prev => ({ ...prev, leaveType: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Types</option>
                      {leaveTypes.map(type => (
                        <option key={type._id} value={type._id}>{type.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Employee Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Employee</label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search employees..."
                        value={employeeSearch}
                        onChange={(e) => setEmployeeSearch(e.target.value)}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {employeeSearch && (
                        <button
                          onClick={() => {
                            setEmployeeSearch('');
                            setFilters(prev => ({ ...prev, employee: '' }));
                          }}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                      {employeeSearch && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                          <div 
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-200"
                            onClick={() => {
                              setFilters(prev => ({ ...prev, employee: '' }));
                              setEmployeeSearch('');
                            }}
                          >
                            All Employees
                          </div>
                          {employees
                            .filter(emp => 
                              `${emp.firstName} ${emp.lastName} ${emp.employeeId}`
                                .toLowerCase()
                                .includes(employeeSearch.toLowerCase())
                            )
                            .slice(0, 10) // Limit to 10 results for performance
                            .map(emp => (
                              <div 
                                key={emp._id}
                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                                onClick={() => {
                                  setFilters(prev => ({ ...prev, employee: emp._id }));
                                  setEmployeeSearch(`${emp.firstName} ${emp.lastName} (${emp.employeeId})`);
                                }}
                              >
                                {emp.firstName} {emp.lastName} ({emp.employeeId})
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Start Date Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                    <input
                      type="date"
                      value={filters.startDate}
                      onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* End Date Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                    <input
                      type="date"
                      value={filters.endDate}
                      onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Search and Actions */}
                <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4">
                  <div className="flex-1 max-w-md">
                    <div className="relative">
                      <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by employee name or ID..."
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setFilters({ status: '', leaveType: '', employee: '', startDate: '', endDate: '', search: '' });
                        setEmployeeSearch('');
                      }}
                      className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Clear Filters
                    </button>
                    <button
                      onClick={() => {
                        // Trigger refetch with current filters
                        refetch();
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>

              {/* Modern Requests Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {leaveRequests.map((request) => (
                  <div key={request._id} className="bg-white rounded-3xl shadow-xl hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 overflow-hidden border border-gray-100 group">
                    {/* Status Banner */}
                    <div className={`h-2 w-full ${
                      request.status === 'approved' ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                      request.status === 'rejected' ? 'bg-gradient-to-r from-red-500 to-rose-500' :
                      request.status === 'pending' ? 'bg-gradient-to-r from-yellow-500 to-amber-500' :
                      'bg-gradient-to-r from-gray-500 to-slate-500'
                    }`}></div>

                    <div className="p-6">
                      {/* Request Header with Status Badge */}
                      <div className="flex items-start justify-between mb-5">
                        <div className="flex items-center space-x-4">
                          <div className="relative">
                            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                              <UserIcon className="h-7 w-7 text-white" />
                            </div>
                            {/* Online indicator */}
                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 border-2 border-white rounded-full"></div>
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900 leading-tight">
                              {request.employeeId.firstName} {request.employeeId.lastName}
                            </h3>
                            <p className="text-sm text-gray-500 font-medium">{request.employeeId.employeeId}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(request.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                        <div className={`px-4 py-2 rounded-full text-xs font-bold shadow-sm border ${
                          request.status === 'approved' ? 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border-green-200' :
                          request.status === 'rejected' ? 'bg-gradient-to-r from-red-50 to-rose-50 text-red-700 border-red-200' :
                          request.status === 'pending' ? 'bg-gradient-to-r from-yellow-50 to-amber-50 text-yellow-700 border-yellow-200' :
                          'bg-gradient-to-r from-gray-50 to-slate-50 text-gray-700 border-gray-200'
                        }`}>
                          <div className="flex items-center space-x-1">
                            {request.status === 'approved' && <CheckCircleIcon className="h-3 w-3" />}
                            {request.status === 'rejected' && <XCircleIcon className="h-3 w-3" />}
                            {request.status === 'pending' && <ClockIcon className="h-3 w-3" />}
                            <span className="capitalize">{request.status}</span>
                          </div>
                        </div>
                      </div>

                      {/* Leave Details with Enhanced Design */}
                      <div className="space-y-4 mb-6">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-100 hover:shadow-md transition-shadow duration-200">
                            <div className="flex items-center space-x-2 mb-2">
                              <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center">
                                <CalendarIcon className="h-3 w-3 text-blue-600" />
                              </div>
                              <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Leave Type</span>
                            </div>
                            <p className="text-sm font-bold text-blue-900 leading-tight">{request.leaveTypeId.name}</p>
                            <p className="text-xs text-blue-600 mt-1">{request.leaveTypeId.code}</p>
                          </div>
                          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-4 border border-purple-100 hover:shadow-md transition-shadow duration-200">
                            <div className="flex items-center space-x-2 mb-2">
                              <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center">
                                <ClockIcon className="h-3 w-3 text-purple-600" />
                              </div>
                              <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Duration</span>
                            </div>
                            <p className="text-sm font-bold text-purple-900 leading-tight">
                              {request.workingDays} days
                            </p>
                            <p className="text-xs text-purple-600 mt-1">
                              {new Date(request.fromDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(request.toDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Reason Preview with Better Design */}
                      {request.reason && (
                        <div className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl border border-gray-200">
                          <div className="flex items-center space-x-2 mb-2">
                            <div className="w-6 h-6 bg-gray-100 rounded-lg flex items-center justify-center">
                              <DocumentTextIcon className="h-3 w-3 text-gray-600" />
                            </div>
                            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Reason</span>
                          </div>
                          <p className="text-sm text-gray-700 line-clamp-2 leading-relaxed">{request.reason}</p>
                        </div>
                      )}

                      {/* Enhanced Action Buttons */}
                      <div className="space-y-4">
                        {/* Primary Actions Row */}
                        {request.status !== 'approved' && request.status !== 'rejected' && (
                          request.approvalChain?.some(chain => chain.status === 'pending') ||
                          (user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'hr_admin') &&
                          request.status === 'submitted'
                        ) && (
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              onClick={() => handleApproveRequest(request)}
                              disabled={approving}
                              className="relative overflow-hidden bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-3 px-4 rounded-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 group/btn"
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 opacity-0 group-hover/btn:opacity-20 transition-opacity duration-300"></div>
                              <div className="flex items-center justify-center space-x-2 relative z-10">
                                <CheckCircleIcon className="h-5 w-5" />
                                <span className="text-sm">Approve</span>
                              </div>
                              {approving && (
                                <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                </div>
                              )}
                            </button>
                            <button
                              onClick={() => handleRejectRequest(request)}
                              disabled={rejecting}
                              className="relative overflow-hidden bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-bold py-3 px-4 rounded-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 group/btn"
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-red-400 to-rose-500 opacity-0 group-hover/btn:opacity-20 transition-opacity duration-300"></div>
                              <div className="flex items-center justify-center space-x-2 relative z-10">
                                <XCircleIcon className="h-5 w-5" />
                                <span className="text-sm">Reject</span>
                              </div>
                              {rejecting && (
                                <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                </div>
                              )}
                            </button>
                          </div>
                        )}

                        {/* Secondary Actions Row */}
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          <button
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowViewModal(true);
                            }}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-xl transition-all duration-200 border border-blue-200 hover:border-blue-300 hover:shadow-md"
                          >
                            <EyeIcon className="h-4 w-4 mr-2" />
                            View Details
                          </button>

                          {(request.status === 'draft' || request.status === 'submitted') && (
                            <button
                              onClick={() => {
                                setSelectedRequest(request);
                                setEditForm({
                                  leaveTypeId: request.leaveTypeId?._id || request.leaveTypeId,
                                  fromDate: request.fromDate ? new Date(request.fromDate).toISOString().split('T')[0] : '',
                                  toDate: request.toDate ? new Date(request.toDate).toISOString().split('T')[0] : '',
                                  reason: request.reason || '',
                                  contactDuringLeave: request.contactDuringLeave || {
                                    phone: '',
                                    email: '',
                                    address: ''
                                  }
                                });
                                setShowEditModal(true);
                              }}
                              className="inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-xl transition-all duration-200 border border-indigo-200 hover:border-indigo-300 hover:shadow-md"
                            >
                              <PencilIcon className="h-4 w-4 mr-2" />
                              Edit
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {leaveRequests.length === 0 && (
                <div className="bg-white rounded-3xl shadow-xl p-12 text-center border border-gray-100">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <ClipboardDocumentListIcon className="h-12 w-12 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">No Leave Requests</h3>
                  <p className="text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">
                    There are no leave requests to display at the moment. New requests will appear here once employees submit them.
                  </p>
                  <button
                    onClick={() => setShowEmployeeRequestModal(true)}
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-2xl hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create First Request
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'types' && (
            <div className="space-y-6">
              {/* Modern Header */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Leave Types Management</h2>
                    <p className="text-gray-600 mt-1">Configure and manage different types of leave</p>
                  </div>
                  <button
                    onClick={() => setShowTypeModal(true)}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Add Leave Type
                  </button>
                </div>
              </div>

              {/* Modern Leave Types Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {leaveTypes.map((type) => {
                  const getTypeColor = (code) => {
                    switch (code) {
                      case 'CL': return { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-800' };
                      case 'SL': return { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600', badge: 'bg-red-100 text-red-800' };
                      case 'EL': return { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600', badge: 'bg-green-100 text-green-800' };
                      case 'ML': return { bg: 'bg-pink-50', border: 'border-pink-200', icon: 'text-pink-600', badge: 'bg-pink-100 text-pink-800' };
                      case 'PL': return { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600', badge: 'bg-purple-100 text-purple-800' };
                      case 'MRG': return { bg: 'bg-yellow-50', border: 'border-yellow-200', icon: 'text-yellow-600', badge: 'bg-yellow-100 text-yellow-800' };
                      case 'BL': return { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'text-gray-600', badge: 'bg-gray-100 text-gray-800' };
                      case 'NH': return { bg: 'bg-indigo-50', border: 'border-indigo-200', icon: 'text-indigo-600', badge: 'bg-indigo-100 text-indigo-800' };
                      case 'OH': return { bg: 'bg-teal-50', border: 'border-teal-200', icon: 'text-teal-600', badge: 'bg-teal-100 text-teal-800' };
                      default: return { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'text-gray-600', badge: 'bg-gray-100 text-gray-800' };
                    }
                  };

                  const colors = getTypeColor(type.code);

                  return (
                    <div key={type._id} className={`${colors.bg} ${colors.border} border-2 rounded-2xl p-6 hover:shadow-xl transform hover:scale-105 transition-all duration-300`}>
                      {/* Type Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors.icon} bg-white shadow-md`}>
                            <CalendarDaysIcon className="h-6 w-6" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">{type.name}</h3>
                            <p className="text-sm font-medium text-gray-600">{type.code}</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end space-y-2">
                          <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${colors.badge}`}>
                            {type.isPaid ? 'Paid' : 'Unpaid'}
                          </span>
                          {type.isStatutory && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                              Statutory
                            </span>
                          )}
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                // Set selected type for editing
                                setSelectedType(type);
                                // Populate form with current type data
                                setTypeForm({
                                  name: type.name,
                                  code: type.code,
                                  countryCode: type.countryCode || 'IN',
                                  description: type.description || '',
                                  isPaid: type.isPaid,
                                  accrualType: type.accrualType || 'yearly',
                                  accrualAmount: type.accrualAmount || 0,
                                  maxCarryForward: type.maxCarryForward || 0,
                                  maxEncashment: type.maxEncashment || 0,
                                  applicationRules: {
                                    minDays: type.applicationRules?.minDays || 1,
                                    maxDays: type.applicationRules?.maxDays || 0,
                                    includeWeekendsInCalculation: type.applicationRules?.includeWeekendsInCalculation || false
                                  }
                                });
                                setShowTypeModal(true);
                              }}
                              className="w-8 h-8 bg-blue-100 hover:bg-blue-200 rounded-lg flex items-center justify-center transition-colors duration-200"
                              title="Edit Leave Type"
                            >
                              <PencilIcon className="h-4 w-4 text-blue-600" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedType(type);
                                setShowDeleteModal(true);
                              }}
                              className="w-8 h-8 bg-red-100 hover:bg-red-200 rounded-lg flex items-center justify-center transition-colors duration-200"
                              title="Delete Leave Type"
                            >
                              <XMarkIcon className="h-4 w-4 text-red-600" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-sm text-gray-700 mb-4 leading-relaxed">{type.description}</p>

                      {/* Key Details */}
                      <div className="space-y-3 mb-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-600">Accrual</span>
                          <span className="text-sm font-bold text-gray-900">
                            {type.accrualAmount} days/{type.accrualType}
                          </span>
                        </div>

                        {type.maxCarryForward > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-600">Max Carry Forward</span>
                            <span className="text-sm font-bold text-gray-900">{type.maxCarryForward} days</span>
                          </div>
                        )}

                        {type.isEncashmentAllowed && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-600">Max Encashment</span>
                            <span className="text-sm font-bold text-gray-900">{type.maxEncashment} days</span>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-600">Min Days</span>
                          <span className="text-sm font-bold text-gray-900">{type.applicationRules?.minDays || 1} day(s)</span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-600">Max Days</span>
                          <span className="text-sm font-bold text-gray-900">
                            {type.applicationRules?.maxDays || 'No limit'}
                          </span>
                        </div>
                      </div>

                      {/* Features */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {type.isCarryForwardAllowed && (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-md">
                            <CheckBadgeIcon className="h-3 w-3 mr-1" />
                            Carry Forward
                          </span>
                        )}
                        {type.isEncashmentAllowed && (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-md">
                            <BanknotesIcon className="h-3 w-3 mr-1" />
                            Encashable
                          </span>
                        )}
                        {type.applicationRules?.isPartialDayAllowed && (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-md">
                            <ClockIcon className="h-3 w-3 mr-1" />
                            Half Day
                          </span>
                        )}
                        {type.applicationRules?.includeWeekendsInCalculation && (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-md">
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            Includes Weekends
                          </span>
                        )}
                      </div>

                      {/* Eligibility */}
                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Eligibility:</span>
                          <span className="font-medium text-gray-900">
                            {type.eligibility?.applicableGender === 'all' ? 'All' : type.eligibility?.applicableGender}
                          </span>
                        </div>
                        {type.eligibility?.minServiceDays > 0 && (
                          <div className="flex items-center justify-between text-xs mt-1">
                            <span className="text-gray-500">Min Service:</span>
                            <span className="font-medium text-gray-900">{type.eligibility.minServiceDays} days</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {leaveTypes.length === 0 && (
                <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                  <CogIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Leave Types</h3>
                  <p className="text-gray-600">No leave types have been configured yet.</p>
                  <button
                    onClick={() => setShowTypeModal(true)}
                    className="mt-4 inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create First Leave Type
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'allocation' && (
            <div className="space-y-6">
              {/* Modern Header */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Bulk Leave Allocation</h2>
                    <p className="text-gray-600 mt-1">Allocate leave balances to multiple employees at once</p>
                  </div>
                  <button
                    onClick={() => setShowBulkModal(true)}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    <ScaleIcon className="h-5 w-5 mr-2" />
                    Start Bulk Allocation
                  </button>
                </div>
              </div>

              {/* Allocation Instructions */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">How Bulk Allocation Works</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold text-blue-600">1</span>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Select Leave Type</h4>
                        <p className="text-sm text-gray-600">Choose the leave type you want to allocate</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold text-blue-600">2</span>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Choose Allocation Method</h4>
                        <p className="text-sm text-gray-600">Fixed amount or based on service duration</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold text-green-600">3</span>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Select Employees</h4>
                        <p className="text-sm text-gray-600">Choose which employees to allocate to</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold text-green-600">4</span>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Execute Allocation</h4>
                        <p className="text-sm text-gray-600">Apply the allocation to all selected employees</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'carry-forward' && (
            <div className="space-y-6">
              {/* Modern Header */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Carry Forward Leave Balances</h2>
                    <p className="text-gray-600 mt-1">Carry forward unused leave balances from previous year to current year</p>
                  </div>
                  <button
                    onClick={() => setShowCarryForwardModal(true)}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    <ArrowTrendingUpIcon className="h-5 w-5 mr-2" />
                    Carry Forward Leaves
                  </button>
                </div>
              </div>

              {/* Carry Forward Instructions */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">How Carry Forward Works</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold text-blue-600">1</span>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Select From & To Year</h4>
                        <p className="text-sm text-gray-600">Choose the fiscal years for carry forward operation</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold text-blue-600">2</span>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Select Leave Types</h4>
                        <p className="text-sm text-gray-600">Choose which leave types allow carry forward</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold text-green-600">3</span>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Select Employees</h4>
                        <p className="text-sm text-gray-600">Choose specific employees or apply to all eligible</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold text-green-600">4</span>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Execute Carry Forward</h4>
                        <p className="text-sm text-gray-600">Automate the balance transfer process</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Carry Forward History */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Carry Forward Operations</h3>
                {carryForwardHistoryData?.data?.operations?.length > 0 ? (
                  <div className="space-y-4">
                    {(carryForwardHistoryData?.data?.operations || []).slice(0, 3).map((operation) => (
                      <div key={operation._id} className="border border-gray-200 rounded-xl p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                              <CheckBadgeIcon className="h-4 w-4 text-green-600" />
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900">
                                Carry Forward {operation.fromYear} → {operation.toYear}
                              </h4>
                              <p className="text-xs text-gray-500">
                                {new Date(operation.processedAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                          </div>
                          <span className="inline-flex items-center px-3 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                            Completed
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                          <div>
                            <span className="text-gray-500">Employees:</span>
                            <span className="ml-1 font-medium">{operation.employeesCount}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Leave Types:</span>
                            <span className="ml-1 font-medium">{operation.leaveTypesCount}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Total Carried:</span>
                            <span className="ml-1 font-medium">{operation.totalCarriedDays} days</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Caps Applied:</span>
                            <span className="ml-1 font-medium">{operation.capsApplied} employees</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ArrowTrendingUpIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Carry Forward History</h3>
                    <p className="text-gray-600">No carry forward operations have been performed yet.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'employees' && (
            <div className="space-y-3">
              {/* Compact Header with Actions */}
              <div className="bg-white rounded-xl shadow-lg p-4">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Employee Leave Management</h2>
                    <p className="text-xs text-gray-600">View and manage individual employee leave balances</p>
                  </div>
                  <div className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded-md">
                    {(() => {
                      const filteredCount = employees.filter(employee => {
                        if (employeeSearch) {
                          const searchLower = employeeSearch.toLowerCase();
                          const matchesSearch = (
                            employee.firstName.toLowerCase().includes(searchLower) ||
                            employee.lastName.toLowerCase().includes(searchLower) ||
                            employee.employeeId.toLowerCase().includes(searchLower) ||
                            `${employee.firstName} ${employee.lastName}`.toLowerCase().includes(searchLower)
                          );
                          if (!matchesSearch) return false;
                        }

                        if (filters.employmentStatus) {
                          if (employee.employmentStatus !== filters.employmentStatus) {
                            return false;
                          }
                        }

                        return true;
                      }).length;
                      return `${filteredCount}/${employees.length} employees`;
                    })()}
                  </div>
                </div>
              </div>

              {/* Compact Employee Filters */}
              <div className="bg-white rounded-xl shadow-lg p-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Employee Search */}
                  <div className="flex-1">
                    <div className="relative">
                      <MagnifyingGlassIcon className="h-4 w-4 absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search employees..."
                        value={employeeSearch}
                        onChange={(e) => setEmployeeSearch(e.target.value)}
                        className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Employment Status */}
                  <div className="sm:w-48">
                    <select
                      value={filters.employmentStatus || ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, employmentStatus: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="on_leave">On Leave</option>
                      <option value="terminated">Terminated</option>
                    </select>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {(employeeSearch || filters.employmentStatus) && (
                      <button
                        onClick={() => {
                          setEmployeeSearch('');
                          setFilters(prev => ({
                            ...prev,
                            employmentStatus: ''
                          }));
                        }}
                        className="px-3 py-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        Clear
                      </button>
                    )}
                    <button
                      onClick={() => {
                        // Refresh employee data
                        // refetch(); // This will work for employee data if needed
                      }}
                      className="px-3 py-2 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Refresh
                    </button>
                  </div>
                </div>
              </div>

              {/* Filtered Employee Leave Grid */}
              {/* Compute filtered employees for this tab */}
              {(() => {
                // Compute the filtered employees once
                const tabFilteredEmployees = employees.filter(employee => {
                  // Filter by search term (name or employee ID)
                  if (employeeSearch) {
                    const searchLower = employeeSearch.toLowerCase();
                    const matchesSearch = (
                      employee.firstName.toLowerCase().includes(searchLower) ||
                      employee.lastName.toLowerCase().includes(searchLower) ||
                      employee.employeeId.toLowerCase().includes(searchLower) ||
                      `${employee.firstName} ${employee.lastName}`.toLowerCase().includes(searchLower)
                    );
                    if (!matchesSearch) return false;
                  }

                  // Filter by department
                  if (filters.department) {
                    if (!employee.departmentId || employee.departmentId.name !== filters.department) {
                      return false;
                    }
                  }

                  // Filter by employment status
                  if (filters.employmentStatus) {
                    if (employee.employmentStatus !== filters.employmentStatus) {
                      return false;
                    }
                  }

                  return true;
                });

                const filteredCount = tabFilteredEmployees.length;

                return (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {tabFilteredEmployees.map((employee) => (
                        <div key={employee._id} className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 overflow-hidden">
                          <div className="p-6">
                            {/* Employee Header */}
                            <div className="flex items-center space-x-3 mb-4">
                              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                                <UserIcon className="h-6 w-6 text-white" />
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {employee.firstName} {employee.lastName}
                                </h3>
                                <p className="text-sm text-gray-500">{employee.employeeId}</p>
                                <div className="flex items-center space-x-2 mt-1">
                                  {employee.departmentId && (
                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                      {employee.departmentId.name}
                                    </span>
                                  )}
                                  <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                                    employee.employmentStatus === 'active' ? 'bg-green-100 text-green-800' :
                                    employee.employmentStatus === 'inactive' ? 'bg-gray-100 text-gray-800' :
                                    employee.employmentStatus === 'on_leave' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {employee.employmentStatus || 'active'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Quick Actions */}
                            <div className="space-y-2">
                              <button
                                onClick={() => {
                                  setSelectedEmployee(employee);
                                  setLeaveForm(prev => ({ ...prev, employeeId: employee._id }));
                                  setShowEmployeeRequestModal(true);
                                }}
                                className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                                disabled={employee.employmentStatus === 'inactive' || employee.employmentStatus === 'terminated'}
                              >
                                <PlusIcon className="h-4 w-4 mr-2" />
                                Create Leave Request
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedEmployee(employee);
                                  setShowLeaveBalanceModal(true);
                                }}
                                className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                              >
                                <EyeIcon className="h-4 w-4 mr-2" />
                                View Leave Balance
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {tabFilteredEmployees.length === 0 && (
                      <div className="bg-white rounded-2xl shadow-xl p-12 text-center border border-gray-100">
                        <UsersIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Employees Found</h3>
                        <p className="text-gray-600 mb-4">
                          {employees.length === 0
                            ? "There are no employees to display."
                            : "No employees match your current filters. Try adjusting your search criteria."
                          }
                        </p>
                        {(employeeSearch || filters.department || filters.employmentStatus) && (
                          <button
                            onClick={() => {
                              setEmployeeSearch('');
                              setFilters(prev => ({
                                ...prev,
                                department: '',
                                employmentStatus: ''
                              }));
                            }}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Clear Filters
                          </button>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Create Leave Request Modal */}
      {showEmployeeRequestModal && (
        <div className="fixed inset-0 z-40 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-4 text-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"></div>
            <div className="relative bg-white rounded-3xl shadow-2xl transform transition-all max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleCreateEmployeeRequest}>
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6 rounded-t-3xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                        <PlusIcon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Create Leave Request for Employee</h3>
                        <p className="text-blue-100 text-sm">Submit a leave application on behalf of an employee</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowEmployeeRequestModal(false)}
                      className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="px-8 py-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Employee Selection */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Select Employee *
                      </label>
                      <select
                        value={leaveForm.employeeId}
                        onChange={(e) => setLeaveForm(prev => ({ ...prev, employeeId: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        required
                      >
                        <option value="">Select employee</option>
                        {employees.map((employee) => (
                          <option key={employee._id} value={employee._id}>
                            {employee.firstName} {employee.lastName} ({employee.employeeId})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Leave Type */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Leave Type *
                      </label>
                      <select
                        value={leaveForm.leaveTypeId}
                        onChange={(e) => setLeaveForm(prev => ({ ...prev, leaveTypeId: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        required
                      >
                        <option value="">Select leave type</option>
                        {leaveTypes.map((type) => (
                          <option key={type._id} value={type._id}>
                            {type.name} ({type.code})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Current Leave Balance Display */}
                    {leaveForm.employeeId && leaveForm.leaveTypeId && (
                      <div className="md:col-span-2">
                        <div className={`p-4 rounded-xl border-2 ${
                          currentLeaveBalance && currentLeaveBalance.available > 0
                            ? 'bg-green-50 border-green-200'
                            : 'bg-red-50 border-red-200'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900">Current Leave Balance</h4>
                              <p className={`text-lg font-bold ${
                                currentLeaveBalance && currentLeaveBalance.available > 0
                                  ? 'text-green-700'
                                  : 'text-red-700'
                              }`}>
                                {currentLeaveBalance ? (
                                  <>
                                    {currentLeaveBalance.available} days available
                                    {currentLeaveBalance.available <= 0 && (
                                      <span className="block text-sm font-normal text-red-600 mt-1">
                                        ⚠️ Cannot create leave request - insufficient balance
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  'No balance record found'
                                )}
                              </p>
                            </div>
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              currentLeaveBalance && currentLeaveBalance.available > 0
                                ? 'bg-green-100'
                                : 'bg-red-100'
                            }`}>
                              {currentLeaveBalance && currentLeaveBalance.available > 0 ? (
                                <CheckCircleIcon className="h-6 w-6 text-green-600" />
                              ) : (
                                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* From Date */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        From Date *
                      </label>
                      <input
                        type="date"
                        value={leaveForm.fromDate}
                        onChange={(e) => setLeaveForm(prev => ({ ...prev, fromDate: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        required
                      />
                    </div>

                    {/* To Date */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        To Date *
                      </label>
                      <input
                        type="date"
                        value={leaveForm.toDate}
                        onChange={(e) => setLeaveForm(prev => ({ ...prev, toDate: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        required
                      />
                    </div>
                  </div>

                  {/* Reason */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Reason for Leave *
                    </label>
                    <textarea
                      value={leaveForm.reason}
                      onChange={(e) => setLeaveForm(prev => ({ ...prev, reason: e.target.value }))}
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white resize-none"
                      placeholder="Please provide a detailed reason for the leave request..."
                      required
                    />
                  </div>

                  {/* Admin Options */}
                  <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
                    <h4 className="text-lg font-semibold text-blue-900 mb-4">Admin Options</h4>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="deductBalanceImmediately"
                        checked={leaveForm.deductBalanceImmediately}
                        onChange={(e) => setLeaveForm(prev => ({ ...prev, deductBalanceImmediately: e.target.checked }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="deductBalanceImmediately" className="ml-3 text-sm font-medium text-blue-900">
                        <span className="font-semibold">Deduct balance immediately</span>
                        <span className="block text-xs text-blue-700 mt-1">
                          Auto-approve the request and deduct leave balance without requiring approval workflow
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={leaveForm.contactDuringLeave.phone}
                          onChange={(e) => setLeaveForm(prev => ({
                            ...prev,
                            contactDuringLeave: { ...prev.contactDuringLeave, phone: e.target.value }
                          }))}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                          placeholder="+91 XXXXX XXXXX"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          value={leaveForm.contactDuringLeave.email}
                          onChange={(e) => setLeaveForm(prev => ({
                            ...prev,
                            contactDuringLeave: { ...prev.contactDuringLeave, email: e.target.value }
                          }))}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                          placeholder="employee.email@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Address
                        </label>
                        <input
                          type="text"
                          value={leaveForm.contactDuringLeave.address}
                          onChange={(e) => setLeaveForm(prev => ({
                            ...prev,
                            contactDuringLeave: { ...prev.contactDuringLeave, address: e.target.value }
                          }))}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                          placeholder="Employee's location during leave"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="bg-gray-50 px-8 py-6 rounded-b-3xl flex items-center justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowEmployeeRequestModal(false)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creatingRequest || (currentLeaveBalance && currentLeaveBalance.available <= 0)}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg"
                  >
                    {creatingRequest ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Creating...
                      </div>
                    ) : currentLeaveBalance && currentLeaveBalance.available <= 0 ? (
                      'Insufficient Balance'
                    ) : (
                      'Create Request'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Allocation Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-40 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-4 text-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"></div>
            <div className="relative bg-white rounded-3xl shadow-2xl transform transition-all max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleBulkAllocation}>
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-8 py-6 rounded-t-3xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                        <ScaleIcon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Bulk Leave Allocation</h3>
                        <p className="text-green-100 text-sm">Allocate leave balances to multiple employees</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowBulkModal(false)}
                      className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="px-8 py-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Leave Type */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Leave Type *
                      </label>
                      <select
                        value={bulkForm.leaveTypeId}
                        onChange={(e) => setBulkForm(prev => ({ ...prev, leaveTypeId: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        required
                      >
                        <option value="">Select leave type</option>
                        {leaveTypes.map((type) => (
                          <option key={type._id} value={type._id}>
                            {type.name} ({type.code})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Allocation Type */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Allocation Type *
                      </label>
                      <select
                        value={bulkForm.allocationType}
                        onChange={(e) => setBulkForm(prev => ({ ...prev, allocationType: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                      >
                        <option value="fixed">Fixed Amount</option>
                        <option value="percentage">Percentage of Annual</option>
                        <option value="service_based">Service Based</option>
                      </select>
                    </div>

                    {/* Allocation Value */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Allocation Value *
                      </label>
                      <input
                        type="number"
                        value={bulkForm.allocationValue}
                        onChange={(e) => setBulkForm(prev => ({ ...prev, allocationValue: parseInt(e.target.value) || 0 }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        min="0"
                        placeholder={bulkForm.allocationType === 'percentage' ? 'e.g., 12' : 'e.g., 12'}
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {bulkForm.allocationType === 'percentage' ? 'Percentage of annual entitlement' :
                         bulkForm.allocationType === 'service_based' ? 'Days per year of service' :
                         'Fixed number of days'}
                      </p>
                    </div>

                    {/* Year */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Allocation Year *
                      </label>
                      <input
                        type="number"
                        value={bulkForm.year}
                        onChange={(e) => setBulkForm(prev => ({ ...prev, year: parseInt(e.target.value) || new Date().getFullYear() }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        min="2000"
                        max="2100"
                        required
                      />
                    </div>
                  </div>

                  {/* Employee Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Select Employees *
                    </label>
                    <div className="border-2 border-gray-200 rounded-xl p-4 bg-gray-50 max-h-60 overflow-y-auto">
                      <div className="grid grid-cols-1 gap-2">
                        {employees.map((employee) => (
                          <label key={employee._id} className="flex items-center space-x-3 p-2 hover:bg-white rounded-lg transition-colors duration-200">
                            <input
                              type="checkbox"
                              checked={bulkForm.employeeIds.includes(employee._id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setBulkForm(prev => ({
                                    ...prev,
                                    employeeIds: [...prev.employeeIds, employee._id]
                                  }));
                                } else {
                                  setBulkForm(prev => ({
                                    ...prev,
                                    employeeIds: prev.employeeIds.filter(id => id !== employee._id)
                                  }));
                                }
                              }}
                              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                                <UserIcon className="h-4 w-4 text-white" />
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-900">
                                  {employee.firstName} {employee.lastName}
                                </span>
                                <span className="text-xs text-gray-500 ml-2">
                                  ({employee.employeeId})
                                </span>
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Selected: {bulkForm.employeeIds.length} employees
                    </p>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="bg-gray-50 px-8 py-6 rounded-b-3xl flex items-center justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowBulkModal(false)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updatingBalance || bulkForm.employeeIds.length === 0}
                    className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-emerald-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg"
                  >
                    {updatingBalance ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Allocating...
                      </div>
                    ) : (
                      `Allocate to ${bulkForm.employeeIds.length} Employees`
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Create Leave Type Modal */}
      {showTypeModal && (
        <div className="fixed inset-0 z-40 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-4 text-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"></div>
            <div className="relative bg-white rounded-3xl shadow-2xl transform transition-all max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleCreateLeaveType}>
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6 rounded-t-3xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                        <PlusIcon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white text-left">Create Leave Type</h3>
                        <p className="text-blue-100 text-sm">Add a new leave type configuration</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowTypeModal(false)}
                      className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="px-8 py-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div>
                      <label className="block text-left text-sm font-semibold text-gray-900 mb-3">
                        Leave Type Name *
                      </label>
                      <input
                        type="text"
                        value={typeForm.name}
                        onChange={(e) => handleNameChange(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        placeholder="e.g., Annual Leave"
                        required
                      />
                    </div>

                    {/* Code */}
                    <div>
                      <label className="block text-left text-sm font-semibold text-gray-900 mb-3">
                        Code *
                      </label>
                      <input
                        type="text"
                        value={typeForm.code}
                        onChange={(e) => setTypeForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        placeholder="e.g., AL"
                        required
                      />
                    </div>
                  </div>

                  {/* Country Code */}
                  <div>
                    <label className="block text-left text-sm font-semibold text-gray-900 mb-3">
                      Country Code *
                    </label>
                    <select
                      value={typeForm.countryCode}
                      onChange={(e) => setTypeForm(prev => ({ ...prev, countryCode: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                      required
                    >
                      <option value="IN">India (IN)</option>
                      <option value="US">United States (US)</option>
                      <option value="UK">United Kingdom (UK)</option>
                      <option value="AE">United Arab Emirates (AE)</option>
                      <option value="CA">Canada (CA)</option>
                      <option value="AU">Australia (AU)</option>
                    </select>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-left text-sm font-semibold text-gray-900 mb-3">
                      Description
                    </label>
                    <textarea
                      value={typeForm.description}
                      onChange={(e) => setTypeForm(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white resize-none"
                      placeholder="Describe the purpose and rules for this leave type..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Accrual Type */}
                    <div>
                      <label className="block text-left text-sm font-semibold text-gray-900 mb-3">
                        Accrual Type *
                      </label>
                      <select
                        value={typeForm.accrualType}
                        onChange={(e) => setTypeForm(prev => ({ ...prev, accrualType: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                      >
                        <option value="yearly">Yearly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="manual">Manual</option>
                      </select>
                    </div>

                    {/* Accrual Amount */}
                    <div>
                      <label className="block text-left text-sm font-semibold text-gray-900 mb-3">
                        Accrual Amount *
                      </label>
                      <input
                        type="number"
                        value={typeForm.accrualAmount}
                        onChange={(e) => setTypeForm(prev => ({ ...prev, accrualAmount: parseInt(e.target.value) || 0 }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        min="0"
                        placeholder="e.g., 12"
                      />
                    </div>
                  </div>

                  {/* Carry Forward, Encashment, Min/Max Days */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Max Carry Forward */}
                    <div>
                      <label className="block text-left text-sm font-semibold text-gray-900 mb-3">
                        Max Carry Forward (days)
                      </label>
                      <input
                        type="number"
                        value={typeForm.maxCarryForward}
                        onChange={(e) => setTypeForm(prev => ({ ...prev, maxCarryForward: parseInt(e.target.value) || 0 }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        min="0"
                        placeholder="e.g., 30"
                      />
                    </div>

                    {/* Max Encashment */}
                    <div>
                      <label className="block text-left text-sm font-semibold text-gray-900 mb-3">
                        Max Encashment (days)
                      </label>
                      <input
                        type="number"
                        value={typeForm.maxEncashment}
                        onChange={(e) => setTypeForm(prev => ({ ...prev, maxEncashment: parseInt(e.target.value) || 0 }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        min="0"
                        placeholder="e.g., 15"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Min Days */}
                    <div>
                      <label className="block text-left text-sm font-semibold text-gray-900 mb-3">
                        Min Days per Request *
                      </label>
                      <input
                        type="number"
                        value={typeForm.applicationRules.minDays}
                        onChange={(e) => setTypeForm(prev => ({
                          ...prev,
                          applicationRules: {
                            ...prev.applicationRules,
                            minDays: parseInt(e.target.value) || 1
                          }
                        }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        min="1"
                        placeholder="e.g., 1"
                      />
                    </div>

                    {/* Max Days */}
                    <div>
                      <label className="block text-left text-sm font-semibold text-gray-900 mb-3">
                        Max Days per Request
                      </label>
                      <input
                        type="number"
                        value={typeForm.applicationRules.maxDays}
                        onChange={(e) => setTypeForm(prev => ({
                          ...prev,
                          applicationRules: {
                            ...prev.applicationRules,
                            maxDays: parseInt(e.target.value) || 0
                          }
                        }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        min="0"
                        placeholder="0 = no limit"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        0 = no limit on maximum days per request
                      </p>
                    </div>
                  </div>

                  {/* Paid Leave Checkbox */}
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={typeForm.isPaid}
                      onChange={(e) => setTypeForm(prev => ({ ...prev, isPaid: e.target.checked }))}
                      className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="text-sm font-semibold text-gray-900">
                      This is a Paid Leave Type
                    </label>
                  </div>

                  {/* Include Weekends in Calculation Checkbox */}
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={typeForm.applicationRules?.includeWeekendsInCalculation || false}
                      onChange={(e) => setTypeForm(prev => ({
                        ...prev,
                        applicationRules: {
                          ...prev.applicationRules,
                          includeWeekendsInCalculation: e.target.checked
                        }
                      }))}
                      className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="text-sm font-semibold text-gray-900">
                      Include Weekends in Working Days Calculation
                    </label>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="bg-gray-50 px-8 py-6 rounded-b-3xl flex items-center justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowTypeModal(false)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creatingType}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg"
                  >
                    {creatingType ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Creating...
                      </div>
                    ) : (
                      'Create Leave Type'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Leave Balance Modal */}
      {showLeaveBalanceModal && selectedEmployee && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-4 text-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"></div>
            <div className="relative bg-white rounded-3xl shadow-2xl transform transition-all max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-8 py-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                      <ScaleIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Leave Balance Details</h3>
                      <p className="text-green-100 text-sm">
                        {selectedEmployee.firstName} {selectedEmployee.lastName} - {selectedEmployee.employeeId}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowLeaveBalanceModal(false);
                      setSelectedEmployee(null);
                    }}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="px-8 py-6">
                {balancesLoading || typesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                    <span className="ml-3 text-gray-600">Loading leave balances...</span>
                  </div>
                ) : balancesError ? (
                  <div className="text-center py-12">
                    <ExclamationTriangleIcon className="h-16 w-16 text-red-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Balances</h3>
                    <p className="text-gray-600">{balancesError?.message || 'Failed to load leave balances'}</p>
                  </div>
                ) : leaveBalances.length === 0 ? (
                  <div className="text-center py-12">
                    <ScaleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Leave Balances</h3>
                    <p className="text-gray-600">This employee doesn't have any leave balances allocated yet.</p>
                  </div>
                ) : typesLoading ? (
                  <div className="text-center py-12">
                    <ScaleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Loading Leave Types</h3>
                    <p className="text-gray-600">Please wait while we load the leave type information...</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-blue-600">Total Allocated</p>
                            <p className="text-2xl font-bold text-blue-900">
                              {leaveBalances && leaveBalances.length > 0 ? leaveBalances.reduce((sum, balance) => sum + (balance.allocated || 0), 0) : 0} days
                            </p>
                          </div>
                          <BanknotesIcon className="h-8 w-8 text-blue-600" />
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-green-600">Total Used</p>
                            <p className="text-2xl font-bold text-green-900">
                              {leaveBalances && leaveBalances.length > 0 ? leaveBalances.reduce((sum, balance) => sum + (balance.used || 0), 0) : 0} days
                            </p>
                          </div>
                          <ArrowTrendingDownIcon className="h-8 w-8 text-green-600" />
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-purple-600">Available Balance</p>
                            <p className="text-2xl font-bold text-purple-900">
                              {leaveBalances && leaveBalances.length > 0 ? leaveBalances.reduce((sum, balance) => sum + (balance.available || 0), 0) : 0} days
                            </p>
                          </div>
                          <CheckBadgeIcon className="h-8 w-8 text-purple-600" />
                        </div>
                      </div>
                    </div>

                    {/* Leave Balance Details */}
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                        <h4 className="text-lg font-semibold text-gray-900">Leave Balance Breakdown</h4>
                        <p className="text-sm text-gray-600">Detailed view of all leave types and their balances</p>
                      </div>

                      <div className="divide-y divide-gray-200">
                        {leaveBalances && leaveBalances.length > 0 ? leaveBalances.map((balance) => {
                          // Use populated leave type data if available, otherwise find from leaveTypes array
                          const leaveType = balance.leaveTypeId && typeof balance.leaveTypeId === 'object' 
                            ? balance.leaveTypeId 
                            : leaveTypes.find(type => type._id === balance.leaveTypeId);
                          
                          const availablePercentage = balance.allocated > 0 ? ((balance.available / balance.allocated) * 100) : 0;

                          return (
                            <div key={balance._id} className="px-6 py-4 hover:bg-gray-50 transition-colors duration-200">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-3">
                                  <div className={`w-3 h-3 rounded-full ${leaveType?.isPaid ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                  <div>
                                    <h5 className="text-sm font-semibold text-gray-900">
                                      {leaveType?.name || 'Unknown Leave Type'}
                                    </h5>
                                    <p className="text-xs text-gray-500">{leaveType?.code || 'N/A'}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-medium text-gray-900">
                                    {balance.available} / {balance.allocated} days
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {availablePercentage.toFixed(1)}% remaining
                                  </p>
                                </div>
                              </div>

                              {/* Progress Bar */}
                              <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                                <div
                                  className={`h-2 rounded-full transition-all duration-300 ${
                                    availablePercentage > 50 ? 'bg-green-500' :
                                    availablePercentage > 25 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${Math.min(availablePercentage, 100)}%` }}
                                ></div>
                              </div>

                              {/* Balance Details */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                                <div>
                                  <span className="text-gray-500">Allocated:</span>
                                  <span className="ml-1 font-medium text-gray-900">{balance.allocated}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Used:</span>
                                  <span className="ml-1 font-medium text-gray-900">{balance.used}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Carried Forward:</span>
                                  <span className="ml-1 font-medium text-gray-900">{balance.carriedForward}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Encashed:</span>
                                  <span className="ml-1 font-medium text-gray-900">{balance.encashed}</span>
                                </div>
                              </div>
                            </div>
                          );
                        }) : (
                          <div className="text-center py-8">
                            <p className="text-gray-500">No leave balances to display</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-8 py-6 rounded-b-3xl flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowLeaveBalanceModal(false);
                    setSelectedEmployee(null);
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold rounded-xl hover:from-gray-700 hover:to-gray-800 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showViewModal && selectedRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowViewModal(false)}></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-3xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full max-w-full mx-4">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-xl bg-white bg-opacity-20 flex items-center justify-center">
                      <DocumentTextIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Leave Request Details</h3>
                      <p className="text-blue-100 text-sm">Application #{selectedRequest.applicationNumber}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowViewModal(false)}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="px-8 py-6">
                <div className="space-y-6">
                  {/* Employee Information */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Employee Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm font-medium text-gray-500">Employee Name</span>
                        <p className="text-sm font-semibold text-gray-900">
                          {selectedRequest.employeeId?.firstName} {selectedRequest.employeeId?.lastName}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Employee ID</span>
                        <p className="text-sm font-semibold text-gray-900">{selectedRequest.employeeId?.employeeId}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Email</span>
                        <p className="text-sm font-semibold text-gray-900">{selectedRequest.employeeId?.email}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Phone</span>
                        <p className="text-sm font-semibold text-gray-900">{selectedRequest.employeeId?.phone}</p>
                      </div>
                    </div>
                  </div>

                  {/* Leave Details */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Leave Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm font-medium text-gray-500">Leave Type</span>
                        <p className="text-sm font-semibold text-gray-900">
                          {selectedRequest.leaveTypeId?.name} ({selectedRequest.leaveTypeId?.code})
                        </p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Status</span>
                        <div className="mt-1">
                          <span className={`inline-flex items-center px-3 py-1 text-sm font-bold rounded-full ${
                            selectedRequest.status === 'approved' ? 'bg-green-100 text-green-800 border-2 border-green-200' :
                            selectedRequest.status === 'rejected' ? 'bg-red-100 text-red-800 border-2 border-red-200' :
                            selectedRequest.status === 'pending' ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-200' :
                            'bg-gray-100 text-gray-800 border-2 border-gray-200'
                          }`}>
                            {selectedRequest.status === 'approved' && <CheckCircleIcon className="h-4 w-4 mr-1" />}
                            {selectedRequest.status === 'rejected' && <XCircleIcon className="h-4 w-4 mr-1" />}
                            {selectedRequest.status === 'pending' && <ClockIcon className="h-4 w-4 mr-1" />}
                            {selectedRequest.status.charAt(0).toUpperCase() + selectedRequest.status.slice(1)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">From Date</span>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatDate(selectedRequest.fromDate)}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">To Date</span>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatDate(selectedRequest.toDate)}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Total Days</span>
                        <p className="text-sm font-semibold text-gray-900">{selectedRequest.totalDays}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Working Days</span>
                        <p className="text-sm font-semibold text-gray-900">{selectedRequest.workingDays}</p>
                      </div>
                    </div>
                  </div>

                  {/* Reason */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Reason</h4>
                    <p className="text-sm text-gray-700">{selectedRequest.reason}</p>
                  </div>

                  {/* Contact Information */}
                  {selectedRequest.contactDuringLeave && (
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Contact During Leave</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <span className="text-sm font-medium text-gray-500">Phone</span>
                          <p className="text-sm font-semibold text-gray-900">{selectedRequest.contactDuringLeave.phone}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Email</span>
                          <p className="text-sm font-semibold text-gray-900">{selectedRequest.contactDuringLeave.email}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Address</span>
                          <p className="text-sm font-semibold text-gray-900">{selectedRequest.contactDuringLeave.address}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Approval Chain */}
                  {selectedRequest.approvalChain && selectedRequest.approvalChain.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                          <UsersIcon className="h-4 w-4 text-blue-600" />
                        </div>
                        Approval Status
                        {selectedRequest.approvalChain.some(approval => approval.remarks) && (
                          <span className="ml-2 inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            <ChatBubbleLeftRightIcon className="h-3 w-3 mr-1" />
                            Has Remarks
                          </span>
                        )}
                      </h4>
                      <div className="space-y-3">
                        {selectedRequest.approvalChain.map((approval, index) => (
                          <div key={index} className="p-4 bg-white rounded-xl border border-gray-200 hover:shadow-md transition-shadow duration-200">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center space-x-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  approval.status === 'approved' ? 'bg-green-100' :
                                  approval.status === 'rejected' ? 'bg-red-100' :
                                  'bg-yellow-100'
                                }`}>
                                  {approval.status === 'approved' ? (
                                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                  ) : approval.status === 'rejected' ? (
                                    <XCircleIcon className="h-5 w-5 text-red-600" />
                                  ) : (
                                    <ClockIcon className="h-5 w-5 text-yellow-600" />
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-bold text-gray-900">{approval.approverName}</p>
                                  <p className="text-xs text-gray-500">{approval.approverDesignation}</p>
                                </div>
                              </div>
                              <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                                approval.status === 'approved' ? 'bg-green-100 text-green-800 border border-green-200' :
                                approval.status === 'rejected' ? 'bg-red-100 text-red-800 border border-red-200' :
                                'bg-yellow-100 text-yellow-800 border border-yellow-200'
                              }`}>
                                {approval.status.charAt(0).toUpperCase() + approval.status.slice(1)}
                              </span>
                            </div>
                            {approval.decidedAt && (
                              <p className="text-xs text-gray-500 mb-2 flex items-center">
                                <ClockIcon className="h-3 w-3 mr-1" />
                                {formatDateTime(approval.decidedAt)}
                              </p>
                            )}
                            {approval.remarks && (
                              <div className="mt-3 p-3 bg-gray-50 rounded-lg border-l-4 border-gray-300">
                                <p className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                  <ChatBubbleLeftRightIcon className="h-3 w-3 mr-1" />
                                  Remarks:
                                </p>
                                <p className="text-sm text-gray-600 leading-relaxed">{approval.remarks}</p>
                              </div>
                            )}
                          </div>
                ))}
              </div>

              {/* Pagination Controls */}
              {pagination.totalPages > 1 && (
                <div className="bg-white rounded-2xl shadow-xl p-6">
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div className="text-sm text-gray-600">
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
                        disabled={pagination.page <= 1}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>

                      {/* Page Numbers */}
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        const pageNum = Math.max(1, Math.min(pagination.totalPages - 4, pagination.page - 2)) + i;
                        if (pageNum > pagination.totalPages) return null;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 text-sm font-medium rounded-lg ${
                              pagination.page === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      <button
                        onClick={() => handlePageChange(Math.min(pagination.totalPages, pagination.page + 1))}
                        disabled={pagination.page >= pagination.totalPages}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}                  {/* Final Decision */}
                  {selectedRequest.finalDecision && (
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                          selectedRequest.finalDecision.status === 'approved' ? 'bg-green-100' : 'bg-red-100'
                        }`}>
                          {selectedRequest.finalDecision.status === 'approved' ? (
                            <CheckCircleIcon className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircleIcon className="h-4 w-4 text-red-600" />
                          )}
                        </div>
                        Final Decision
                      </h4>
                      <div className="p-4 bg-white rounded-xl border border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-sm font-medium text-gray-900">Decision</span>
                          <span className={`inline-flex items-center px-4 py-2 text-sm font-bold rounded-full ${
                            selectedRequest.finalDecision.status === 'approved' 
                              ? 'bg-green-100 text-green-800 border-2 border-green-200' 
                              : 'bg-red-100 text-red-800 border-2 border-red-200'
                          }`}>
                            {selectedRequest.finalDecision.status === 'approved' ? (
                              <CheckCircleIcon className="h-4 w-4 mr-2" />
                            ) : (
                              <XCircleIcon className="h-4 w-4 mr-2" />
                            )}
                            {selectedRequest.finalDecision.status.charAt(0).toUpperCase() + selectedRequest.finalDecision.status.slice(1)}
                          </span>
                        </div>
                        {selectedRequest.finalDecision.decidedAt && (
                          <div className="mb-4">
                            <span className="text-sm font-medium text-gray-500">Decision Date:</span>
                            <p className="text-sm text-gray-900 flex items-center mt-1">
                              <ClockIcon className="h-4 w-4 mr-2 text-gray-400" />
                              {formatDateTime(selectedRequest.finalDecision.decidedAt)}
                            </p>
                          </div>
                        )}
                        {selectedRequest.finalDecision.remarks && (
                          <div className="mt-4 p-4 bg-gray-50 rounded-lg border-l-4 border-gray-300">
                            <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                              <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                              Decision Remarks:
                            </p>
                            <p className="text-sm text-gray-600 leading-relaxed">{selectedRequest.finalDecision.remarks}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Request Timeline */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Request Timeline</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <PlusIcon className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">Request Created</div>
                          <div className="text-xs text-gray-500">
                            {formatDateTime(selectedRequest.createdAt)}
                          </div>
                        </div>
                      </div>

                      {selectedRequest.status !== 'pending' && (
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            selectedRequest.status === 'approved' ? 'bg-green-100' : 'bg-red-100'
                          }`}>
                            {selectedRequest.status === 'approved' ? (
                              <CheckCircleIcon className="h-4 w-4 text-green-600" />
                            ) : (
                              <XCircleIcon className="h-4 w-4 text-red-600" />
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              Request {selectedRequest.status === 'approved' ? 'Approved' : 'Rejected'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {selectedRequest.finalDecision?.decidedAt 
                                ? formatDateTime(selectedRequest.finalDecision.decidedAt)
                                : selectedRequest.approvalChain && selectedRequest.approvalChain.length > 0
                                  ? formatDateTime(selectedRequest.approvalChain[selectedRequest.approvalChain.length - 1].decidedAt)
                                  : selectedRequest.processedAt 
                                    ? formatDateTime(selectedRequest.processedAt)
                                    : 'Date not available'
                              }
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-8 py-6 rounded-b-3xl flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => setShowViewModal(false)}
                  className="px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold rounded-xl hover:from-gray-700 hover:to-gray-800 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Leave Request Modal */}
      {showEditModal && selectedRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowEditModal(false)}></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-3xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-xl bg-white bg-opacity-20 flex items-center justify-center">
                      <PencilIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Edit Leave Request</h3>
                      <p className="text-purple-100 text-sm">Application #{selectedRequest.applicationNumber}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="px-8 py-6">
                <form onSubmit={handleEditRequest} className="space-y-6">
                  {/* Leave Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Leave Type *
                    </label>
                    <select
                      value={editForm.leaveTypeId}
                      onChange={(e) => setEditForm(prev => ({ ...prev, leaveTypeId: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    >
                      <option value="">Select leave type</option>
                      {leaveTypes.map((type) => (
                        <option key={type._id} value={type._id}>
                          {type.name} ({type.code})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date Range */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        From Date *
                      </label>
                      <input
                        type="date"
                        value={editForm.fromDate}
                        onChange={(e) => setEditForm(prev => ({ ...prev, fromDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        To Date *
                      </label>
                      <input
                        type="date"
                        value={editForm.toDate}
                        onChange={(e) => setEditForm(prev => ({ ...prev, toDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      />
                    </div>
                  </div>

                  {/* Reason */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason *
                    </label>
                    <textarea
                      value={editForm.reason}
                      onChange={(e) => setEditForm(prev => ({ ...prev, reason: e.target.value }))}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Please provide a reason for your leave request"
                      required
                    />
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900">Contact During Leave</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone
                        </label>
                        <input
                          type="tel"
                          value={editForm.contactDuringLeave.phone}
                          onChange={(e) => setEditForm(prev => ({
                            ...prev,
                            contactDuringLeave: { ...prev.contactDuringLeave, phone: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          value={editForm.contactDuringLeave.email}
                          onChange={(e) => setEditForm(prev => ({
                            ...prev,
                            contactDuringLeave: { ...prev.contactDuringLeave, email: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Address
                        </label>
                        <input
                          type="text"
                          value={editForm.contactDuringLeave.address}
                          onChange={(e) => setEditForm(prev => ({
                            ...prev,
                            contactDuringLeave: { ...prev.contactDuringLeave, address: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-8 py-6 rounded-b-3xl flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-3 bg-gray-600 text-white font-semibold rounded-xl hover:bg-gray-700 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onClick={handleEditRequest}
                  disabled={updatingRequest}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg disabled:opacity-50"
                >
                  {updatingRequest ? 'Updating...' : 'Update Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedRequest && (
        <div className="fixed inset-0 z-40 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75 backdrop-blur-sm" onClick={() => setShowApprovalModal(false)}></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              {/* Status Banner */}
              <div className={`h-2 w-full ${
                approvalAction === 'approve' ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-red-500 to-rose-500'
              }`}></div>

              {/* Modal Header */}
              <div className={`bg-gradient-to-r px-8 py-6 rounded-t-3xl ${approvalAction === 'approve' ? 'from-green-600 to-emerald-600' : 'from-red-600 to-rose-600'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 rounded-2xl bg-white bg-opacity-20 flex items-center justify-center shadow-lg">
                      {approvalAction === 'approve' ? (
                        <CheckCircleIcon className="h-7 w-7 text-white" />
                      ) : (
                        <XCircleIcon className="h-7 w-7 text-white" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">
                        {approvalAction === 'approve' ? 'Approve' : 'Reject'} Leave Request
                      </h3>
                      <p className={`text-sm mt-1 ${approvalAction === 'approve' ? 'text-green-100' : 'text-red-100'}`}>
                        {selectedRequest.employeeId?.firstName} {selectedRequest.employeeId?.lastName} • {selectedRequest.employeeId?.employeeId}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowApprovalModal(false)}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-xl p-2 transition-colors duration-200"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="px-8 py-6">
                {/* Request Summary Card */}
                <div className="mb-6 bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-6 border border-gray-200">
                  <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                      <DocumentTextIcon className="h-4 w-4 text-gray-600" />
                    </div>
                    Leave Request Summary
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <CalendarIcon className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Leave Type</p>
                          <p className="text-sm font-bold text-blue-900">{selectedRequest.leaveTypeId?.name}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <ClockIcon className="h-4 w-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Duration</p>
                          <p className="text-sm font-bold text-purple-900">{selectedRequest.workingDays} working days</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <CalendarIcon className="h-4 w-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Date Range</p>
                          <p className="text-sm font-bold text-emerald-900">
                            {new Date(selectedRequest.fromDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(selectedRequest.toDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                          <UserIcon className="h-4 w-4 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Employee</p>
                          <p className="text-sm font-bold text-amber-900">{selectedRequest.employeeId?.firstName} {selectedRequest.employeeId?.lastName}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {selectedRequest.reason && (
                    <div className="mt-4 p-4 bg-white rounded-xl border border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Reason</p>
                      <p className="text-sm text-gray-700 leading-relaxed">{selectedRequest.reason}</p>
                    </div>
                  )}
                </div>

                {/* Comments Form */}
                <form onSubmit={handleApprovalSubmit}>
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-3 flex items-center">
                      <div className={`w-6 h-6 rounded-lg flex items-center justify-center mr-2 ${
                        approvalAction === 'approve' ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {approvalAction === 'approve' ? (
                          <CheckCircleIcon className="h-3 w-3 text-green-600" />
                        ) : (
                          <XCircleIcon className="h-3 w-3 text-red-600" />
                        )}
                      </div>
                      {approvalAction === 'approve' ? 'Approval' : 'Rejection'} Comments
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <textarea
                      value={approvalForm.remarks}
                      onChange={(e) => setApprovalForm(prev => ({ ...prev, remarks: e.target.value }))}
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white resize-none"
                      placeholder={`Please provide ${approvalAction === 'approve' ? 'approval' : 'rejection'} comments...`}
                      required
                    />
                  </div>
                </form>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-8 py-6 rounded-b-3xl flex items-center justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowApprovalModal(false)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-2xl hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleApprovalSubmit}
                  disabled={approving || rejecting}
                  className={`relative overflow-hidden px-8 py-3 font-bold rounded-2xl focus:ring-2 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 ${
                    approvalAction === 'approve'
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 focus:ring-green-500'
                      : 'bg-gradient-to-r from-red-500 to-rose-600 text-white hover:from-red-600 hover:to-rose-700 focus:ring-red-500'
                  }`}
                >
                  <div className="absolute inset-0 bg-gradient-to-r opacity-0 hover:opacity-20 transition-opacity duration-300 ${
                    approvalAction === 'approve' ? 'from-green-400 to-emerald-500' : 'from-red-400 to-rose-500'
                  }"></div>
                  <div className="flex items-center justify-center space-x-2 relative z-10">
                    {approving || rejecting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        {approvalAction === 'approve' ? (
                          <CheckCircleIcon className="h-5 w-5" />
                        ) : (
                          <XCircleIcon className="h-5 w-5" />
                        )}
                        <span>{approvalAction === 'approve' ? 'Approve' : 'Reject'} Request</span>
                      </>
                    )}
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Carry Forward Modal */}
      {showCarryForwardModal && (
        <div className="fixed inset-0 z-40 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-4 text-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"></div>
            <div className="relative bg-white rounded-3xl shadow-2xl transform transition-all max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleCarryForward}>
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-6 rounded-t-3xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                        <ArrowTrendingUpIcon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Carry Forward Leave Balances</h3>
                        <p className="text-purple-100 text-sm">Transfer unused leave balance to the next year</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowCarryForwardModal(false)}
                      className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="px-8 py-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* From Year */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        From Year *
                      </label>
                      <input
                        type="number"
                        value={carryForwardForm.fromYear}
                        onChange={(e) => setCarryForwardForm(prev => ({ ...prev, fromYear: parseInt(e.target.value) || new Date().getFullYear() - 1 }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        min="2000"
                        max="2100"
                        required
                      />
                    </div>

                    {/* To Year */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        To Year *
                      </label>
                      <input
                        type="number"
                        value={carryForwardForm.toYear}
                        onChange={(e) => setCarryForwardForm(prev => ({ ...prev, toYear: parseInt(e.target.value) || new Date().getFullYear() }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        min="2000"
                        max="2100"
                        required
                      />
                    </div>
                  </div>

                  {/* Leave Type Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Leave Types to Carry Forward *
                    </label>
                    <div className="border-2 border-gray-200 rounded-xl p-4 bg-gray-50 max-h-60 overflow-y-auto">
                      <div className="grid grid-cols-1 gap-3">
                        {leaveTypes
                          .filter(type => type.isCarryForwardAllowed)
                          .map((type) => (
                          <label key={type._id} className="flex items-center space-x-3 p-3 hover:bg-white rounded-lg transition-colors duration-200">
                            <input
                              type="checkbox"
                              checked={carryForwardForm.leaveTypeId === type._id}
                              onChange={(e) => setCarryForwardForm(prev => ({
                                ...prev,
                                leaveTypeId: e.target.checked ? type._id : ''
                              }))}
                              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                            />
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs font-semibold">
                                  {type.code}
                                </span>
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-900">
                                  {type.name}
                                </span>
                                <p className="text-xs text-gray-500">
                                  Max Carry Forward: {type.maxCarryForward || 'No limit'} days
                                </p>
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                      {leaveTypes.filter(type => type.isCarryForwardAllowed).length === 0 && (
                        <div className="text-center py-8">
                          <p className="text-gray-500">No leave types allow carry forward</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Employee Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Select Employees
                    </label>
                    <div className="space-y-3">
                      {/* Apply to All Eligible Option */}
                      <label className="flex items-center space-x-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl hover:shadow-md transition-shadow duration-200 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={carryForwardForm.applyToAllEligible}
                          onChange={(e) => setCarryForwardForm(prev => ({
                            ...prev,
                            applyToAllEligible: e.target.checked,
                            employeeIds: e.target.checked ? [] : prev.employeeIds
                          }))}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <div className="flex items-center space-x-3">
                          <UsersIcon className="h-5 w-5 text-green-600" />
                          <div>
                            <span className="text-sm font-medium text-gray-900">
                              Apply to All Eligible Employees
                            </span>
                            <p className="text-xs text-gray-600">
                              Automatically apply to employees with carryable leave balances
                            </p>
                          </div>
                        </div>
                      </label>

                      {/* Specific Employee Selection */}
                      {!carryForwardForm.applyToAllEligible && (
                        <div className="border-2 border-gray-200 rounded-xl p-4 bg-gray-50 max-h-60 overflow-y-auto">
                          <div className="grid grid-cols-1 gap-2">
                            {employees.slice(0, 10).map((employee) => (
                              <label key={employee._id} className="flex items-center space-x-3 p-2 hover:bg-white rounded-lg transition-colors duration-200">
                                <input
                                  type="checkbox"
                                  checked={carryForwardForm.employeeIds.includes(employee._id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setCarryForwardForm(prev => ({
                                        ...prev,
                                        employeeIds: [...prev.employeeIds, employee._id]
                                      }));
                                    } else {
                                      setCarryForwardForm(prev => ({
                                        ...prev,
                                        employeeIds: prev.employeeIds.filter(id => id !== employee._id)
                                      }));
                                    }
                                  }}
                                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                />
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                                    <UserIcon className="h-4 w-4 text-white" />
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-gray-900">
                                      {employee.firstName} {employee.lastName}
                                    </span>
                                    <span className="text-xs text-gray-500 ml-2">
                                      ({employee.employeeId})
                                    </span>
                                  </div>
                                </div>
                              </label>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Selected: {carryForwardForm.employeeIds.length} employees
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="bg-gray-50 px-8 py-6 rounded-b-3xl flex items-center justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowCarryForwardModal(false)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!carryForwardForm.leaveTypeId || (carryForwardForm.applyToAllEligible ? false : carryForwardForm.employeeIds.length === 0)}
                    className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-indigo-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg"
                  >
                    {!carryForwardForm.leaveTypeId || (carryForwardForm.applyToAllEligible ? false : carryForwardForm.employeeIds.length === 0)
                      ? 'Select Required Fields'
                      : `Carry Forward Leaves`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Leave Type Modal */}
      {showDeleteModal && selectedType && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"></div>
            <div className="relative bg-white rounded-3xl shadow-2xl transform transition-all max-w-lg w-full mx-4">
              {/* Warning Banner */}
              <div className="h-2 w-full bg-gradient-to-r from-red-500 to-rose-500"></div>

              {/* Modal Header */}
              <div className="bg-gradient-to-r from-red-600 to-rose-600 px-8 py-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                      <ExclamationTriangleIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Delete Leave Type</h3>
                      <p className="text-red-100 text-sm">This action cannot be undone</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowDeleteModal(false)}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="px-8 py-6">
                <div className="space-y-6">
                  {/* Warning Message */}
                  <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl">
                    <div className="flex items-start space-x-3">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-bold text-red-900 mb-2">
                          Warning: Irreversible Action
                        </h4>
                        <p className="text-sm text-red-700 leading-relaxed">
                          Deleting this leave type will permanently remove it from the system. All existing leave balances and requests associated with this leave type may be affected.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Leave Type Details */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Leave Type to Delete</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-500">Name:</span>
                        <span className="text-sm font-semibold text-gray-900">{selectedType.name}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-500">Code:</span>
                        <span className="text-sm font-semibold text-gray-900">{selectedType.code}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-500">Type:</span>
                        <span className="text-sm font-semibold text-gray-900">{selectedType.isPaid ? 'Paid' : 'Unpaid'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-500">Allow Carry Forward:</span>
                        <span className="text-sm font-semibold text-gray-900">{selectedType.isCarryForwardAllowed ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Confirmation Text */}
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-3">
                      Type <span className="text-red-600 font-bold">{selectedType.name}</span> to confirm deletion
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                      placeholder={`Type "${selectedType.name}" here...`}
                      value={confirmationText}
                      onChange={(e) => setConfirmationText(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-8 py-6 rounded-b-3xl flex items-center justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteLeaveType}
                  disabled={confirmationText !== selectedType.name}
                  className="px-6 py-3 bg-gradient-to-r from-red-600 to-rose-600 text-white font-semibold rounded-xl hover:from-red-700 hover:to-rose-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg"
                >
                  <ExclamationTriangleIcon className="h-5 w-5 mr-2 inline" />
                  Delete Leave Type
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminLeaveManagement;
